// Tokyo from inside, "Warm room, rain on the glass": the same spring night as tokyo.js and the same railway (that scene
// stands in the street by the viaduct, this one nine floors up). A room in Shimbashi, about a kilometre and a half
// north-east of Tokyo Tower, seen from the back of the room at an angle: the window wall runs away to the right into
// the corner, the side wall comes back towards the viewer. The view looks south-west, so from left to right there are
// the bay with the Rainbow Bridge (to the south), Hamamatsucho's towers, Tokyo Tower, and beyond it the towers of
// Azabudai and Roppongi. Below the window the Shuto expressway curves away and a train crosses the viaduct twice a
// loop. Rain beads and runs on the glass; none of it falls in the room.
// The room is low and thick-walled: a ceiling close over the window head, a deep reveal, heavy timber frames, linen
// curtains, a floor lamp in the corner and a lamp on the desk. Someone sits at the desk with headphones on, writing,
// and looks up when the train passes; a cat watches from the sill; tea steams; a record turns.
// The billboard is a floodlit rooftop sign across the street, left of the tower and whole inside the middle pane.
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, hands, FONT3, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                             // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= The room's geometry: a camera 3.9 m from the window wall, turned 18 degrees along it ================= */
// A room point is (du, v, w): du m along the window wall from the corner (negative: to the left), v m above the
// floor, w m out from that wall into the room (negative: into the wall's thickness, towards the glass). The side
// wall is the plane du = 0. Everything in the room is placed with P(), so it all shares one perspective.
const EYE = 1.12, HORIZON = 160, FOCAL = 380, CX = 240;   // a seated eye; its level on screen; 65 degrees across
const UX = 0.9506, UZ = 0.3107;                      // the window wall's direction in camera space (right, forward)
const CORNER = { X: 1.9, Z: 4.75 };                  // the room's corner in camera space (m): 80 px a metre there
const depth = (du, w) => CORNER.Z + du * UZ - w * UX;
const P = (du, v, w = 0) => { const Z = depth(du, w); return [CX + FOCAL * (CORNER.X + du * UX + w * UZ) / Z, HORIZON - FOCAL * (v - EYE) / Z]; };
/** du of screen column x on the plane w m out from the window wall, and the row of height v there */
const duAt = (x, w = 0) => { const k = (x - CX) / FOCAL; return (k * (CORNER.Z - w * UX) - CORNER.X - w * UZ) / (UX - k * UZ); };
const yAt = (x, v, w = 0) => HORIZON - FOCAL * (v - EYE) / depth(duAt(x, w), w);
/** w of screen column x on the side wall, and the row of height v there */
const wAtR = x => { const k = (x - CX) / FOCAL; return (k * CORNER.Z - CORNER.X) / (UZ + k * UX); };
const yAtR = (x, v) => HORIZON - FOCAL * (v - EYE) / (CORNER.Z - wAtR(x) * UX);
const CORNER_X = Math.round(P(0, 0)[0]);             // 392: the column where the two walls meet

const CEIL = 2.4;                                    // a low ceiling: the window head is 12 cm under it
const WALL_T = 0.26;                                 // the wall is this thick: the glass sits that far back in it
const WIN = { u0: -3.8, u1: -0.42, sill: 0.8, head: 2.28 };          // the opening in the wall
const FRAME = 0.075;                                 // timber frame members (m)
const GLASS = { top: WIN.head - FRAME, bot: WIN.sill + FRAME };      // the glass's own height span
// the frame's uprights on the glass plane, as [left, right] screen columns (right exclusive): two stiles, two mullions
const UPRIGHTS = [[62, 70], [158, 165], [300, 306], [345, 351]];
const PANES = [0, 1, 2].map(k => ({ x0: UPRIGHTS[k][1], x1: UPRIGHTS[k + 1][0] }));   // three panes of glass
const glassTop = x => Math.ceil(yAt(x, GLASS.top, -WALL_T)), glassBot = x => Math.floor(yAt(x, GLASS.bot, -WALL_T));   // rows [top, bot)
const inGlass = (x, y) => PANES.some(q => x >= q.x0 && x < q.x1) && y >= glassTop(x) && y < glassBot(x);
// the outside is prerendered for this box only (everything else is the room)
const OX = 56, OY = 40, OW = 300, OH = 156;

/* ================= Layout: outside ================= */
const TWR = { cx: 232, base: 162, tip: 69, main: [118, 122], top: [91, 93] };   // Tokyo Tower: 93 px, its decks at 0.45 and 0.75
const BRIDGE = { x0: 64, x1: 100, deck: 155, tw: [73, 92] };     // the Rainbow Bridge, across the bay
const BB = { x: 168, y: 127, w: 42, h: 18 };         // the billboard's face, whole inside the middle pane
const BB_LAMPS = [7, 21, 35];                        // floodlamps over its top edge (x offsets)
const BB_FLICK = [[66.2, 67.6], [187.4, 188.3]];     // the right-hand floodlamp stutters
const TRAIN = { len: 86, t: [20, 140], dur: 13 };    // a train crosses left to right at 20 s, right to left at 140 s, as in tokyo.js
const LIFT = { n: 4 };                               // the tower's lift goes up and comes down four times a loop

/* ================= Layout: the room ================= */
const DESK = { u0: -3.78, u1: -0.72, top: 0.72, w0: 0.02, w1: 0.62 };
const SEAT = { du: -2.0, w: 0.72 };                  // where the chair stands (the middle of its seat)
const DLAMP = { du: -1.2,  w: 0.22 };                // the desk lamp's foot
const FLAMP = { du: -0.2, w: 0.3 };                  // the floor lamp, in the corner
const MUSH = { du: -3.62, w: 0.3 };                  // a small lamp at the desk's far end
const SIDE = { w0: 0.55, top: 0.74, deep: 0.42 };    // the sideboard along the side wall, the record player on it
const SHELF = { v: 1.52, w0: 0.34, w1: 1.5 };        // a shelf on the side wall

/* ================= The loop's timeline (seconds) =================
   20, 140            a train crosses the viaduct below (13 s) and its light slides along the ceiling; whoever is at
                      the desk looks up at it from 2 s in
   14, 74, 134, 194   the lift's light leaves the tower's foot, and is at the main deck 30 s later
   48                 a jet crosses high up (40 s)
   66, 187            the billboard's right-hand floodlamp stutters
   36, 96, 156, 216   a sip of tea (5 s): the mug leaves the desk
   75, 195            the cat turns its head towards the tower (8 s)
   110, 230           one of its ears flicks; the tip of its tail flicks up at 27, 87, 147, 207
   every 1.5 s        the towers' red lamps blink together (160 a loop)
   every 30 s         the reader across the street turns a page                                                  */
const LOOK_UP = [[22, 34], [142, 154]];
const SIP = [36, 96, 156, 216], SIP_DUR = 5;
const CAT_TURN = [[75, 83], [195, 203]];
const EAR_TW = [110.2, 230.4], TAIL_FL = [27, 87, 147, 207];
const PLANE = { t0: 48, t1: 88 };
const blinkI = t => { const f = frac(t / 1.5); return f < 0.1 ? f / 0.1 : Math.max(0, 1 - (f - 0.1) / 0.45); };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const mixHex = (a, b, t) => { const A = C(a), B = C(b); return toHex(...A.map((v, i) => Math.round(lerp(v, B[i], t)))); };
/** an ImageData-backed painter for everything drawn ahead */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    clear(i, j) { if (i >= 0 && j >= 0 && i < w && j < h) d[(j * w + i) * 4 + 3] = 0; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2]]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    /** mix what is there towards `col` by f (only where something is) */
    tint(i, j, col, f) {
      i = Math.round(i); j = Math.round(j);
      if (!this.alpha(i, j)) return;
      const c0 = this.get(i, j), v = typeof col === 'string' ? C(col) : col;
      this.set(i, j, [0, 1, 2].map(k => Math.round(lerp(c0[k], v[k], f))));
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1; `soft` < 1 narrows the dithered band between two tones */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only) */
function vnoise(x, y, s) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const a = hash(xi, yi, s), b = hash(xi + 1, yi, s), c = hash(xi, yi + 1, s), d = hash(xi + 1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
function inPoly(px, py, pts) {
  let c = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const [xi, yi] = pts[i], [xj, yj] = pts[j];
    if ((yi > py) !== (yj > py) && px < (xj - xi) * (py - yi) / (yj - yi) + xi) c = !c;
  }
  return c;
}
/** fn(x, y) for every pixel whose centre lies inside the polygon */
function fillPoly(pts, fn) {
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  for (let y = Math.floor(Math.min(...ys)); y <= Math.ceil(Math.max(...ys)); y++)
    for (let x = Math.floor(Math.min(...xs)); x <= Math.ceil(Math.max(...xs)); x++)
      if (inPoly(x + 0.5, y + 0.5, pts)) fn(x, y);
}
function line(p, x0, y0, x1, y1, col) {
  const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
  for (let k = 0; k <= n; k++) p.set(Math.round(lerp(x0, x1, k / n)), Math.round(lerp(y0, y1, k / n)), col);
}
/** a sprite from rows of letters and their colours ('.' clear), mirrored when `flip` */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** a dithered soft disc, for steam */
function makePuff(r, col, maxA) {
  const s = r * 2 + 1, p = pix(s, s), c = C(col);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.2) * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(Math.min(1, q) * maxA * 255));
  }
  return p.done();
}

/* ================= Palettes ================= */
const SKY = ['#06061c', '#090926', '#0d0b30', '#120d3a', '#180f44', '#1f114d', '#281355', '#32155c',
  '#3e1762', '#4c1966', '#5c1b69', '#6d1e6b', '#7f236b', '#91296a', '#a13269', '#af3d69', '#bc4a6b'];
const PLASTER = ['#1d120e', '#2a1911', '#382115', '#472a19', '#57341d', '#683f22', '#7b4c28', '#8f5a2f', '#a36a38',
  '#b67b43', '#c68e52', '#d4a266', '#e0b67e'];          // the walls: warm sand plaster in lamp light
const COOLP = ['#191422', '#221a30', '#2c2140', '#392a50', '#48345f', '#5a406c'];   // plaster where only the city's light falls
const CEILP = ['#170e0b', '#20140e', '#2b1a12', '#372116', '#44291a', '#52321f', '#623d25', '#74492c', '#875734'];
const WOOD = ['#120a07', '#1c100a', '#28170e', '#362012', '#462a17', '#58351d', '#6b4224', '#80502c', '#966035'];   // walnut: the window
const OAK = ['#24160c', '#352011', '#482c17', '#5d3a1e', '#744a26', '#8c5c2f', '#a5703a', '#bc8648', '#d19d5a', '#e2b470'];   // the desk
const FLOORP = ['#120b08', '#190f0a', '#21140d', '#2a1910', '#342014', '#3f2718', '#4b2f1c'];
const LINEN = ['#2a1708', '#3e220b', '#55300f', '#6e4014', '#88531b', '#a16824', '#b87f31', '#cb9743', '#dbae5c'];   // mustard curtains
const NAVY = ['#0c1018', '#121824', '#1a2233', '#232e44', '#2e3c57', '#3c4d6b'];       // the jumper at the desk
const LEAF = ['#0b1a10', '#12291a', '#1b3d22', '#265428', '#356c30', '#4b863a', '#66a044'];
const T_OR = ['#3e120c', '#6c1e0e', '#a03012', '#d04a1a', '#f4701f', '#ff9a48', '#ffc884'];   // the tower's orange, floodlit
const T_WH = ['#523028', '#8a5c4c', '#be8a72', '#e6b89a', '#ffdcc0', '#fff0e0', '#ffffff'];   // and its white bands
const FAR_B = ['#191431', '#1d1736', '#221b3d'];

/* ================= Outside, seen through the glass ================= */
const blit = o => ctx.drawImage(o.c, o.x, o.y);
/** crop a painter to its opaque bounding box (build time); draw the result with blit() */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
function renderSky() {
  const p = pix(OW, OH);
  for (let j = 0; j < OH; j++) for (let i = 0; i < OW; i++) {
    const x = OX + i, y = OY + j;
    let v = Math.pow(clamp((y - 28) / 134, 0, 1), 1.3) * 0.8;
    v += Math.max(0, 1 - Math.hypot((x - TWR.cx) / 170, (y - 170) / 78)) * 0.3;   // the city's glow, strongest behind the tower
    p.set(i, j, pick(SKY, v, x, y));
  }
  return p.done();
}
/** a tileable bank of low cloud, 480 px wide: dark on top, lit pink from below by the city, its edges dithered into the sky */
function makeClouds(seed, h, pal, banks) {
  const r = rng(seed), puffs = [], D = new Float32Array(W * h), p = pix(W, h);
  for (let b = 0; b < banks; b++) {
    const bx = r() * W, bw = 70 + r() * 120, base = h * (0.5 + r() * 0.3);
    for (let i = 0, n = 6 + Math.floor(r() * 6); i < n; i++) {
      const rx = 11 + r() * 14, ry = 3 + r() * 3.4;
      puffs.push([bx + (r() - 0.5) * bw, base - r() * ry * 1.3, rx, ry, base + ry * 0.3]);
    }
  }
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const [qx, qy, rx, ry, base] of puffs) {
      let dx = x - qx; dx -= Math.round(dx / W) * W;
      let v = 1 - dx * dx / (rx * rx) - (y - qy) * (y - qy) / (ry * ry);
      if (y > base) v -= (y - base) * 0.5;
      if (v > best) best = v;
    }
    D[y * W + x] = best + 0.15 * Math.sin(TAU * 11 * x / W + y * 0.5 + seed) * Math.sin(TAU * 5 * x / W - y * 0.3);
  }
  const at = (x, y) => (y < 0 || y >= h ? -9 : D[y * W + ((x % W) + W) % W]);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.5, v = D[y * W + x];
    if (v + th <= 0.05) continue;
    let col = v > 0.55 + th ? pal.core : pal.body;
    if (at(x, y + 3) <= 0.05 + (bayer(x, y) - 0.3) * 0.9) col = pal.bot;        // the belly, dithered up into the body
    p.set(x, y, col);
  }
  return p.done();
}

/* ---- the far shore, the bay and the Rainbow Bridge ---- */
const BAY_X = 112;                                    // the bay shows left of this column, past Hamamatsucho's towers
function renderFar() {
  const p = pix(W, H), r = rng(23), wins = [];
  for (let y = 157; y < 170; y++) for (let x = OX; x < BAY_X + 8; x++) p.set(x, y, (x * 3 + y) % 7 === 0 ? '#1c1c40' : '#121230');   // the bay
  let x = OX;
  while (x < OX + OW) {
    const bw = 4 + Math.floor(r() * 9), shore = x < BAY_X;                 // over the bay only its far shore shows
    const top = shore ? 153 + Math.floor(r() * 3) : 146 + Math.floor(r() * 10), bot = shore ? 157 : 170;
    for (let y = top; y < bot; y++) for (let i = x; i < x + bw; i++) p.set(i, y, y === top ? '#2c2352' : i === x ? '#241d45' : FAR_B[(i + y) % 3 ? 0 : 1]);
    for (let wy = top + 2; wy < bot - 1; wy += 3) for (let wx = x + 1; wx < x + bw - 1; wx += 2)
      if (r() < 0.3) wins.push({ id: wins.length, x: wx, y: wy, n: 2 + Math.floor(r() * 6), p: 0.55, off: r(), col: r() < 0.6 ? '#b88862' : '#8a92c6' });
    x += bw;
  }
  // the Rainbow Bridge: two white towers, the deck, and the lights along its cables
  const { x0, x1, deck, tw } = BRIDGE, top = deck - 9;
  for (let i = x0; i <= x1 + 6; i++) { p.set(i, deck, '#beb8de'); p.set(i, deck + 1, '#3c3862'); }
  for (const tx of tw) for (let y = top; y <= deck + 2; y++) { p.set(tx, y, '#f0eefc'); p.set(tx + 1, y, y === top + 3 ? '#f0eefc' : '#a8a2cc'); }
  const cable = [];
  for (let i = x0; i <= x1 + 6; i++) {
    const [a, b] = tw, mid = (a + b + 1) / 2, half = (b - a) / 2;
    const y = i < a ? lerp(deck - 1, top, (i - x0) / (a - x0)) : i > b + 1 ? lerp(top, deck - 1, (i - b - 1) / (x1 + 5 - b)) : top + 7 * (1 - ((i - mid) / half) ** 2);
    if (i !== a && i !== a + 1 && i !== b && i !== b + 1 && (i & 1)) cable.push([i, Math.round(y)]);
  }
  return { c: trim(p), wins, cable };
}

/* ---- high-rises round the tower (their window floors switch on and off) ---- */
// Hamamatsucho's towers left of it; right of it, further off, the towers of Azabudai and Roppongi
const HRISE = [
  { x: 113, w: 13, top: 114, crown: 'step', cool: 0.7, blink: true }, { x: 127, w: 10, top: 104, crown: 'slant', cool: 0.5, blink: true },
  { x: 139, w: 15, top: 121, cool: 0.4, blink: true }, { x: 177, w: 11, top: 106, crown: 'twin', cool: 0.8, blink: true },
  { x: 258, w: 11, top: 127, cool: 0.5 }, { x: 270, w: 15, top: 112, crown: 'slant', cool: 0.6, blink: true }, { x: 288, w: 10, top: 134, cool: 0.3 },
  { x: 312, w: 12, top: 124, crown: 'step', cool: 0.5, blink: true }, { x: 329, w: 14, top: 136, cool: 0.4 },
];
function hrTop(b, i) {                                // roof line of building b at local column i
  if (b.crown === 'step') return b.top + (i < 3 || i >= b.w - 3 ? 4 : 0);
  if (b.crown === 'twin') return b.top + (i >= b.w / 2 - 2 && i < b.w / 2 + 2 ? 5 : 0);
  if (b.crown === 'slant') return b.top + Math.round((b.w - 1 - i) * 0.45);
  return b.top;
}
function renderHighrises() {
  const p = pix(W, H), lit = pix(W, H), r = rng(41), segs = [], blinks = [];
  const warm = ['#ffd694', '#ffc878', '#ffe2b0'], cool = ['#cfe6ff', '#b4d0ff', '#e6f2ff'];
  for (const b of HRISE) {
    for (let i = 0; i < b.w; i++) for (let y = hrTop(b, i); y < 172; y++) {
      let col = (i & 1) ? '#1a1636' : '#1d1939';
      if (i === 0) col = '#2e2858'; else if (i === b.w - 1) col = '#120f26';
      if (y === hrTop(b, i)) col = '#3c3468';
      p.set(b.x + i, y, col);
    }
    if (b.blink) blinks.push([b.x + 1, hrTop(b, 1) - 1], [b.x + b.w - 2, hrTop(b, b.w - 2) - 1]);
    for (let y = b.top + 4; y < 168; y += 3) {
      let x = b.x + 2;
      while (x < b.x + b.w - 2) {
        const len = Math.min(b.x + b.w - 2 - x, 3 + Math.floor(r() * 6)), col = (r() < b.cool ? cool : warm)[Math.floor(r() * 3)];
        let any = false;
        for (let k = 0; k < len; k += 2) {
          if (y < hrTop(b, x + k - b.x) + 3) continue;
          p.set(x + k, y, '#0f0c22'); lit.set(x + k, y, col); any = true;
        }
        if (any) segs.push({ id: segs.length, x, y, w: len, n: 2 + Math.floor(r() * 7), p: 0.3 + r() * 0.4, off: r() });
        x += len;
      }
    }
  }
  return { c: trim(p), lit: lit.done(), segs, blinks };
}

/* ---- Tokyo Tower: an orange and white lattice floodlit from below, two decks, the antenna ---- */
// 93 px for its 333 m: the main deck (150 m) 0.45 of the way up, the top deck (250 m) 0.75, the last quarter the
// mast; 29 px across the feet, between the 80 m of a face and the 113 m of a corner view
const TKEY = [[162, 14.3], [152, 11.3], [143, 8.7], [133, 6.7], [126, 5.5], [120, 4.8], [115, 3.8], [107, 3], [100, 2.4], [93, 1.9], [89, 1.5], [84, 1.2], [80, 0.8]];
function towerHW(y) {
  if (y >= TKEY[0][0]) return TKEY[0][1];
  for (let i = 0; i < TKEY.length - 1; i++) {
    const [y0, w0] = TKEY[i], [y1, w1] = TKEY[i + 1];
    if (y <= y0 && y >= y1) return w0 + (w1 - w0) * (y0 - y) / (y0 - y1);
  }
  return 0;
}
const TWHITE = [[139, 141], [126, 128], [112, 115], [104, 105], [97, 98], [86, 88]];   // the white bands
const TRUNGS = [162, 157, 152, 147, 143, 141, 139, 134, 130, 128, 126, 115, 112, 108, 105, 104, 100, 98, 97, 94, 88, 86, 84];
function renderTower() {
  const p = pix(W, H), cx = TWR.cx;
  for (let y = 81; y <= TWR.base; y++) {
    if ((y >= TWR.main[0] - 1 && y <= TWR.main[1]) || (y >= TWR.top[0] && y <= TWR.top[1])) continue;
    const hw = towerHW(y + 0.5), fh = Math.floor(hw), ramp = TWHITE.some(([a, b]) => y >= a && y <= b) ? T_WH : T_OR, rung = TRUNGS.includes(y);
    // the Landmark Light: hardest just under each deck and at the feet
    const flood = y > TWR.main[1] ? 0.55 * smooth(146, 124, y) + 0.3 * smooth(150, 162, y) : y > TWR.top[1] ? 0.45 * smooth(112, 96, y) : 0.35;
    for (let dx = -fh; dx <= fh; dx++) {
      const ax = Math.abs(dx), L = dx < 0;
      if (y > 149) {                                   // the arch between the feet
        const e = (dx / 8.4) ** 2 + ((TWR.base + 1 - y) / 13) ** 2;
        if (e < 1) continue;
        if (e < 1.35) { p.set(cx + dx, y, pick(ramp, ((L ? 4.3 : 3.5) + flood) / 6, cx + dx, y)); continue; }
      }
      let lv;
      if (ax === fh) lv = L ? 4.4 : 3.5;              // the outer legs
      else if (ax === 0) lv = 5;                       // the corner leg facing us catches the floodlight
      else if (hw < 3.6) lv = rung || (y & 1) ? (L ? 3.4 : 2.7) : (L ? 2.8 : 2.2);
      else {                                           // diagonal truss, rungs at the band edges
        if (!(rung || ((ax + y) & 3) === 0 || ((ax - y) & 3) === 0)) { p.set(cx + dx, y, ramp === T_WH ? '#44201e' : '#380e0e', 150); continue; }
        lv = L ? 3.3 : 2.5;
      }
      p.set(cx + dx, y, pick(ramp, (lv + flood) / 6, cx + dx, y));
    }
  }
  const [m0, m1] = TWR.main;                           // the main deck: a lit band of two floors
  for (let dx = -6; dx <= 6; dx++) {
    const L = dx < 0, end = Math.abs(dx) === 6;
    if (!end) p.set(cx + dx, m0 - 1, L ? '#f2dcc6' : '#c09a84');
    p.set(cx + dx, m0, L ? '#fff2e0' : '#e8c8b0');
    for (const y of [m0 + 1, m0 + 3]) p.set(cx + dx, y, end ? (L ? '#e8d0b8' : '#a07860') : (dx + 6) % 3 === 0 ? '#9a6848' : L ? '#fff0c0' : '#ffd898');
    p.set(cx + dx, m0 + 2, L ? '#f4e2cc' : '#c8a48a');
    p.set(cx + dx, m1, L ? '#d89a70' : '#a86a4a');
  }
  const [t0, t1] = TWR.top;                            // the top deck: a small cool-white pod
  for (let dx = -3; dx <= 3; dx++) {
    const L = dx < 0;
    p.set(cx + dx, t0, L ? '#fff4e8' : '#e0c0a8');
    p.set(cx + dx, t0 + 1, Math.abs(dx) === 3 ? '#b89480' : L ? '#f0f8ff' : '#c8e0ff');
    p.set(cx + dx, t1, L ? '#d8906a' : '#a0603e');
  }
  for (let y = TWR.tip; y < 81; y++) {                 // the antenna: a candy-striped mast, stouter in its lower half
    const wb = Math.floor((y - TWR.tip) / 3) % 2 === 0;
    p.set(cx, y, wb ? '#fff4e8' : '#ff7a34');
    if (y >= 76) p.set(cx + 1, y, wb ? '#e8c8b0' : '#c84a1e');
  }
  for (let dx = -1; dx <= 2; dx++) p.set(cx + dx, 80, dx < 1 ? '#ffc488' : '#d88a50');
  return trim(p);
}

/* ---- the city at the tower's feet: mid-rise blocks, the park's trees, the expressway and the viaduct ---- */
const XW = { x0: 214, y0: 163, x1: 56, y1: 184 };     // the Shuto expressway: from where it leaves the view to the pane's left edge
const xwAt = s => [XW.x0 + (XW.x1 - XW.x0) * Math.pow(s, 1.45), XW.y0 + (XW.y1 - XW.y0) * Math.pow(s, 1.2) - 2.5 * Math.sin(Math.PI * s)];
const XW_LAMPS = [0.2, 0.33, 0.45, 0.56, 0.66, 0.75, 0.84, 0.92].map(s => xwAt(s).map(Math.round));   // its sodium lamps
function renderMid() {
  const p = pix(W, H), r = rng(57), wins = [];
  let x = OX;
  while (x < OX + OW) {
    const bw = 9 + Math.floor(r() * 16);
    let top = 152 + Math.floor(r() * 12);
    if (x + bw > 210 && x < 254) top = Math.max(top, 165);                // keep the tower's feet clear
    if (x < BAY_X) top = Math.max(top, 163);                              // and the bay under the bridge
    const shade = r() < 0.5 ? '#16122b' : '#19142f';
    for (let y = top; y < 190; y++) for (let i = x; i < Math.min(OX + OW, x + bw); i++)
      p.set(i, y, y === top ? '#2c2548' : i === x ? '#221c3c' : i === x + bw - 1 ? '#100d20' : shade);
    if (r() < 0.4) { const tx = x + 1 + Math.floor(r() * Math.max(1, bw - 5)); p.fill(tx, top - 3, 3, 3, '#221c3a'); p.set(tx, top - 3, '#3a3258'); }   // a tank
    for (let wy = top + 2; wy < 186; wy += 4) for (let wx = x + 2; wx < x + bw - 2; wx += 3) {
      if (r() > 0.5) continue;
      p.fill(wx, wy, 1, 2, '#0f0c1e');
      wins.push({ id: wins.length, x: wx, y: wy, n: 3 + Math.floor(r() * 9), p: 0.35 + r() * 0.3, off: r(), col: r() < 0.65 ? (r() < 0.5 ? '#ffcf80' : '#ffb45e') : '#b0ccff' });
    }
    x += bw;
  }
  for (let i = 206; i < 258; i++) {                                       // the park's trees round the tower's feet
    const h = 2 + Math.round(vnoise(i / 3, 0, 9) * 3);
    for (let y = 164 - h; y < 167; y++) p.set(i, y, hash(i, y, 5) < 0.3 ? '#16302a' : '#0e2020');
  }
  // the expressway: a lit parapet and a dark deck on piers, thicker as it comes near
  for (let k = 0; k <= 600; k++) {
    const s = k / 600, [fx, fy] = xwAt(s), X = Math.round(fx), Y = Math.round(fy), th = 1 + Math.round(s * 2);
    p.set(X, Y, '#6a6492');
    for (let j = 1; j <= th; j++) p.set(X, Y + j, j === th ? '#16132a' : '#2a2648');
    if (X % 15 === 0) for (let j = th + 1; j < th + 4 + Math.round(s * 5); j++) p.set(X, Y + j, '#1a1730');
  }
  for (const [lx, ly] of XW_LAMPS) { p.set(lx, ly - 1, '#3a3458'); p.set(lx, ly - 2, '#3a3458'); p.set(lx, ly - 3, '#ffb060'); }
  return { c: trim(p), wins };
}
const RAIL = { x0: 214, x1: OX + OW, y: 170 };        // the viaduct's deck, right of the building across the street
function renderViaduct() {
  const p = pix(W, H);
  for (let x = RAIL.x0; x < RAIL.x1; x++) {
    if ((x - RAIL.x0) % 34 === 10) for (let y = RAIL.y - 7; y < RAIL.y; y++) p.set(x, y, '#2c2846');   // catenary masts
    p.set(x, RAIL.y, '#5c567f'); p.set(x, RAIL.y + 1, (x & 3) === 0 ? '#3a3660' : '#2c2846'); p.set(x, RAIL.y + 2, '#1c1a30');
    if ((x - RAIL.x0) % 26 < 3) for (let y = RAIL.y + 3; y < 190; y++) p.set(x, y, '#1c1930');
  }
  return trim(p);
}
/** a commuter train: five silver cars with lit windows and a green stripe; `flip` puts its head at the right */
function renderTrain(flip) {
  const p = pix(TRAIN.len, 4), car = 17;
  for (let i = 0; i < TRAIN.len; i++) {
    const m = i % car, c = Math.floor(i / car), x = flip ? TRAIN.len - 1 - i : i;
    if (m === car - 1 && c < 4) continue;                                   // the gap between two cars
    const head = i < 2;
    p.set(x, 0, m === 0 || m === car - 2 ? '#5e5c7a' : '#8a88a6');
    for (const j of [1, 2]) p.set(x, j, head ? '#3a3850' : m === 0 || m === car - 2 || m % 5 === 3 ? '#565470' : j === 1 ? '#fff0c4' : '#ffe2a0');
    p.set(x, 3, m % 2 ? '#6e8c44' : '#5c783a');
  }
  p.set(flip ? TRAIN.len - 1 : 0, 2, '#fff8e0');                            // its headlamp
  return p.done();
}

/* ---- across the street: the building the billboard stands on, a few roofs under the viaduct ---- */
const BLOCK = { x: 160, w: 54, top: 151 };
const ROOFS = [{ x: 224, w: 30, top: 176, tank: 18 }, { x: 256, w: 46, top: 178 }, { x: 304, w: 24, top: 175, mast: 9 }, { x: 330, w: 28, top: 177 }];
function renderNear() {
  const p = pix(W, H);
  for (const b of ROOFS) {
    for (let y = b.top; y < 192; y++) for (let i = b.x; i < b.x + b.w; i++)
      p.set(i, y, y === b.top ? '#3a345e' : y < b.top + 3 ? (hash(i, y, 3) < 0.2 ? '#4a4474' : '#242042') : i === b.x ? '#201b3a' : '#14112a');
    if (b.tank) { p.fill(b.x + b.tank, b.top - 6, 6, 5, '#262144'); for (let j = 0; j < 5; j++) p.set(b.x + b.tank, b.top - 6 + j, '#3e3764'); p.set(b.x + b.tank + 1, b.top - 1, '#1a1730'); p.set(b.x + b.tank + 4, b.top - 1, '#1a1730'); }
    if (b.mast) for (let y = b.top - 9; y < b.top; y++) p.set(b.x + b.mast, y, '#2a2446');
  }
  const { x, w, top } = BLOCK;
  for (let y = top; y < 194; y++) for (let i = x; i < x + w; i++)
    p.set(i, y, y === top ? '#443c6a' : y === top + 1 ? '#2a2448' : i === x ? '#2c2650' : i === x + w - 1 ? '#110e22' : (i & 1) ? '#1b1733' : '#1e1a38');
  for (const f of FLATS) { p.fill(f.x - 1, f.y - 1, FLAT.w + 2, FLAT.h + 2, '#0e0b1e'); p.fill(f.x - 1, f.y + FLAT.h, FLAT.w + 2, 1, '#3a3460'); }   // its windows' frames and sills
  return trim(p);
}
/** what stands on that roof once the billboard is left out: a water tank on its legs */
function renderTank() {
  const p = pix(W, H), x = BLOCK.x + 30, y = BLOCK.top;
  for (let j = 0; j < 9; j++) for (let i = 0; i < 12; i++) p.set(x + i, y - 13 + j, j === 0 ? '#4a426e' : i === 0 ? '#3e3764' : i === 11 ? '#14112a' : (j & 3) === 3 ? '#211c3e' : '#28224a');
  for (const i of [1, 5, 10]) for (let j = 0; j < 4; j++) p.set(x + i, y - 4 + j, '#1a1730');
  return trim(p);
}

/* ---- the billboard: a floodlit poster on a steel frame; its own ad is for an invented yuzu tea ---- */
const BB_NAVY = ['#0b0f2c', '#111840', '#192354', '#24316a', '#31427f'];
const CUP_ROWS = ['.wwwwwww..', '.yyyyyyyc.', '.wwwwwww.c', '.wwwwwwwc.', '..wwwww...', '...sss....', '.sssssss..'];
const CUP_KEY = { w: '#f4ead2', y: '#f2c63c', c: '#d8cab0', s: '#b8a88a' };
/** FONT3 lettering at scale sc with a drop shadow, into a painter */
function ptext(p, s, x, y, sc, col, sh) {
  for (const pass of [0, 1]) {
    let cx = x;
    for (const ch of s) {
      FONT3[ch].forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') p.fill(cx + i * sc + (pass ? 0 : 1), y + j * sc + (pass ? 0 : 1), sc, sc, pass ? col : sh); });
      cx += 4 * sc;
    }
  }
}
function renderBBFace(dark) {                          // `dark`: the floodlamp that is off (-1: none)
  const p = pix(BB.w, BB.h);
  for (let j = 0; j < BB.h; j++) for (let i = 0; i < BB.w; i++) {
    let v = 0;
    BB_LAMPS.forEach((lx, k) => { if (k !== dark) v += Math.max(0, 1 - Math.hypot((i - lx) / 13, (j + 3) / 32)) ** 1.2; });
    p.set(i, j, pick(BB_NAVY, 0.12 + 0.62 * Math.min(1.1, v), i, j, 0.3));
  }
  CUP_ROWS.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (CUP_KEY[row[i]]) p.set(3 + i, 10 + j, CUP_KEY[row[i]]); });
  ptext(p, 'YUZU', 17, 1, 1, '#f6ecd0', '#070a20');
  ptext(p, 'TEA', 17, 7, 2, '#ffd24a', '#7a3a0c');
  for (const [i, j, c] of [[36, 2, '#ffd84a'], [37, 2, '#ffd84a'], [35, 3, '#f2c030'], [36, 3, '#ffe880'], [37, 3, '#ffd84a'], [38, 3, '#e0a820'],
    [35, 4, '#f2c030'], [36, 4, '#ffd84a'], [37, 4, '#ffd84a'], [38, 4, '#e0a820'], [36, 5, '#e0a820'], [37, 5, '#e0a820'], [38, 1, '#5a9a3a'], [39, 1, '#3e7a2c']]) p.set(i, j, c);
  return p.done();
}
function renderBBFrame() {
  const p = pix(W, H), x0 = BB.x - 1, x1 = BB.x + BB.w, y0 = BB.y - 1, y1 = BB.y + BB.h;
  for (const lx of [BB.x + 4, BB.x + 20, BB.x + 36]) for (let y = y1 + 1; y < BLOCK.top; y++) { p.set(lx, y, '#4a4270'); p.set(lx + 1, y, '#1c1832'); }
  for (let i = BB.x + 5; i < BB.x + 36; i++) if ((i + y1) % 4 < 2) p.set(i, y1 + 3, '#342e5c');   // bracing between the legs
  p.fill(x0, y0, BB.w + 2, BB.h + 2, '#1a1428');
  for (let i = x0; i <= x1; i++) { p.set(i, y0, '#3e3458'); p.set(i, y1, '#2c2440'); }
  for (let y = y0; y <= y1; y++) { p.set(x0, y, '#2c2444'); p.set(x1, y, '#110d1e'); }
  for (let i = x0 - 1; i <= x1 + 1; i++) p.set(i, y1 + 1, '#4a4266');                              // the catwalk
  for (const lx of BB_LAMPS) {                                                                     // gooseneck floodlamps
    p.set(BB.x + lx + 1, y0 - 1, '#3a3258'); p.set(BB.x + lx + 1, y0 - 2, '#3a3258');
    p.fill(BB.x + lx - 1, y0 - 4, 3, 1, '#4c4466'); p.fill(BB.x + lx - 1, y0 - 3, 2, 1, '#262238');
  }
  return trim(p);
}

/* ================= Prerendered state ================= */
let HL, SKY_C, CLOUDS, FAR, HR, TOWER, MID, VIA_C, TRAIN_C, NEAR, TANK_C, BB_FRAME, BB_FACE, BB_PITCH, ROOM, PUFF,
  MIST_C, SNOW_C, CURTAIN, CAT_C, CHAIR_C, BODY_C, HEAD_C, MUG_C, HAND_C, GLOW_C, CEIL_C, VIG_C;

/* ================= Per-frame: the city ================= */
function drawScroll(cv, y, k, t) { const off = Math.floor(frac(k * t / LOOP) * W); ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y); }
const lit = (w, t, salt) => hash(w.id + salt, Math.floor(frac(t / LOOP + w.off) * w.n), 3) < w.p;
function drawSky(t) {
  ctx.drawImage(SKY_C, OX, OY);
  for (const L of CLOUDS) { ctx.globalAlpha = L.a; drawScroll(L.c, L.y, L.k, t); }
  ctx.globalAlpha = 1;
  if (t >= PLANE.t0 && t < PLANE.t1) {                 // a jet far away, right to left: a dim body, its lamps, a strobe
    const s = (t - PLANE.t0) / (PLANE.t1 - PLANE.t0), x = Math.round(lerp(OX + OW + 6, OX - 8, s)), y = Math.round(lerp(84, 66, s));
    ctx.fillStyle = '#2a2450'; ctx.fillRect(x - 2, y, 6, 1); ctx.fillRect(x, y - 1, 2, 1);
    ctx.fillStyle = '#ff4a4a'; ctx.fillRect(x - 2, y, 1, 1);
    ctx.fillStyle = '#4aff8a'; ctx.fillRect(x + 3, y + 1, 1, 1);
    const f = frac(t);
    if (f < 0.08 || (f > 0.18 && f < 0.24)) { ctx.fillStyle = '#ffffff'; ctx.fillRect(x + 1, y - 1, 1, 1); halo(HL.strobe, x + 1, y - 1, 0.9); }
  }
}
function drawFar(t) {
  blit(FAR.c);
  for (const w of FAR.wins) if (lit(w, t, 0)) { ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, 1, 1); }
  ctx.fillStyle = '#f4f0ff';                           // the bridge's cable lights, and their shimmer on the bay
  FAR.cable.forEach(([x, y], i) => { ctx.globalAlpha = 0.7 + 0.3 * wave(t, 30 + (i % 7) * 4, i * 0.13); ctx.fillRect(x, y, 1, 1); });
  ctx.fillStyle = '#b8b4e0';
  for (let i = 0; i < 14; i++) {
    const { cyc, u } = cycle(t, 60 + i * 5, i * 0.31);
    ctx.globalAlpha = 0.5 * Math.sin(Math.PI * u);
    ctx.fillRect(BRIDGE.x0 - 2 + Math.floor(hash(i, cyc, 1) * 50), 158 + Math.floor(hash(i, cyc, 2) * 5), 2, 1);
  }
  ctx.globalAlpha = 1;
  halo(HL.bridge, BRIDGE.tw[0], BRIDGE.deck - 5, 0.7); halo(HL.bridge, BRIDGE.tw[1], BRIDGE.deck - 5, 0.7);
}
function drawHighrises(t) {
  blit(HR.c);
  for (const s of HR.segs) if (hash(s.id + 7, Math.floor(frac(t / LOOP + s.off) * s.n), 4) < s.p) ctx.drawImage(HR.lit, s.x, s.y, s.w, 1, s.x, s.y, s.w, 1);
}
function drawTower(t, crowd) {
  const cx = TWR.cx, br = 0.92 + 0.08 * wave(t, 8);
  halo(HL.tower, cx, 138, br); halo(HL.towerMid, cx, 112, br);
  blit(TOWER);
  halo(HL.deck, cx, TWR.main[0] + 2, 0.85); halo(HL.topdeck, cx, TWR.top[0] + 1, 0.8);
  const ly = Math.round(lerp(158, TWR.main[1] + 2, 0.5 - 0.5 * Math.cos(TAU * LIFT.n * (t - 14) / LOOP)));   // the lift's light
  ctx.fillStyle = '#fff6dc'; ctx.fillRect(cx, ly, 1, 2);
  halo(HL.lift, cx, ly, 0.9);
  ctx.fillStyle = '#7a4430';                           // visitors strolling on the main deck
  for (let k = 0; k < 3 && crowd > 0; k++) { const u = frac((3 + k) * t / LOOP + k * 0.27); ctx.fillRect(cx - 5 + Math.floor((1 - Math.abs(2 * u - 1)) * 10), TWR.main[0] + (k & 1 ? 1 : 3), 1, 1); }
}
const CARS = [];
{ const r = rng(301); for (let i = 0; i < 16; i++) CARS.push({ k: 9 + Math.floor(r() * 7), ph: r(), out: i & 1 }); }
function drawMid(t) {
  blit(MID.c);
  for (const w of MID.wins) if (lit(w, t, 90)) { ctx.fillStyle = w.col; ctx.fillRect(w.x, w.y, 1, 2); }
  for (const [lx, ly] of XW_LAMPS) halo(HL.sodium, lx, ly - 3, 0.9);
  for (const c of CARS) {                              // head lamps coming, tail lamps going, quicker as they near
    const u = frac(c.k * t / LOOP + c.ph), s = c.out ? 1 - u : u, [x, y] = xwAt(s);
    ctx.globalAlpha = Math.min(1, s / 0.08, (1 - s) / 0.04);
    ctx.fillStyle = c.out ? '#ff4a3a' : '#fff2cc';
    ctx.fillRect(Math.round(x), Math.round(y) - 1, s > 0.55 ? 2 : 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawBillboard(t, pitch, wet) {
  const off = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(88, Math.floor(t * 20), 3) < 0.55, hum = 0.94 + 0.06 * wave(t, 1700, 0.4);
  halo(HL.bbGlow, BB.x + BB.w / 2, BB.y + 4, (off ? 0.75 : 1) * hum);
  blit(BB_FRAME);
  if (!BB_PITCH.covers(t, pitch)) {
    ctx.drawImage(BB_FACE[off ? 1 : 0], BB.x, BB.y);
    ctx.fillStyle = '#f4ecdc';                         // steam off the painted cup
    for (let k = 0; k < 5; k++) {
      const u = frac(t * 40 / LOOP + k / 5), x = BB.x + 5 + (k % 3) * 2 + Math.round(Math.sin(u * 5 + k) * 1.2), y = BB.y + 9 - Math.round(u * 7);
      ctx.globalAlpha = 0.7 * Math.sin(Math.PI * u);
      ctx.fillRect(x, y, 1, 1);
    }
    ctx.globalAlpha = 1;
  }
  BB_PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, k) => {                        // the lenses and their halos, in every mode
    if (off && k === 2) return;
    ctx.fillStyle = '#fff2d6'; ctx.fillRect(BB.x + lx - 1, BB.y - 3, 2, 1);
    halo(HL.bbLamp, BB.x + lx, BB.y - 2, hum);
  });
  if (wet) {                                           // rain caught in the floodlight, over the sign
    ctx.fillStyle = '#ffe6b8';
    for (let i = 0; i < 10; i++) {
      const { cyc, u } = cycle(t, 1500 + i * 60, i * 0.37), x = BB.x + 2 + Math.floor(hash(i, cyc, 1) * (BB.w - 4));
      ctx.globalAlpha = 0.5 * (1 - u);
      ctx.fillRect(x, BB.y - 12 + Math.floor(u * 9), 1, 2);
    }
    ctx.globalAlpha = 1;
  }
}
function drawTrain(t) {
  for (let k = 0; k < 2; k++) {
    const tau = t - TRAIN.t[k];
    if (tau < 0 || tau >= TRAIN.dur) continue;
    const s = tau / TRAIN.dur, x = Math.round(k ? lerp(RAIL.x1, RAIL.x0 - TRAIN.len, s) : lerp(RAIL.x0 - TRAIN.len, RAIL.x1, s));
    halo(HL.trainWin, x + TRAIN.len / 2, RAIL.y - 2, 0.8);
    ctx.drawImage(TRAIN_C[1 - k], x, RAIL.y - 4);
  }
}
const TVC = ['#3c6cff', '#6d9cff', '#a8ccff', '#2a44b8', '#e2efff', '#5a7ad8'];
function drawNear(board) {
  blit(NEAR);
  if (!board) blit(TANK_C);
}
function drawBlinkers(t, board) {
  const I = blinkI(t);
  if (I <= 0.02) return;
  ctx.fillStyle = I > 0.5 ? '#ff4a3a' : '#b02020';
  const all = [...HR.blinks, [BRIDGE.tw[0], BRIDGE.deck - 10], [BRIDGE.tw[1], BRIDGE.deck - 10],
    [TWR.cx, TWR.tip - 1], [TWR.cx - 3, TWR.top[0] - 1], [TWR.cx + 3, TWR.top[0] - 1], [TWR.cx - 6, TWR.main[0] - 2], [TWR.cx + 6, TWR.main[0] - 2],
    ...(board ? [[BB.x - 1, BB.y - 2], [BB.x + BB.w, BB.y - 2]] : [])];
  for (const [x, y] of all) { ctx.fillRect(x, y, 1, 1); halo(HL.red, x, y, I); }
}

/* ================= The room (painted once; the glass is left clear) ================= */
// Light is worked out in the room's own metres, so it falls off along the walls in the same perspective as they do.
// The lamps: [du, v, w, strength, reach (m)]: the floor lamp in the corner, a small one at the desk's far end
const LAMPS = [[FLAMP.du, 1.55, FLAMP.w, 1, 1.05], [MUSH.du, 0.98, MUSH.w, 0.4, 0.55]];
const lampV = (du, v, w) => LAMPS.reduce((s, [a, b, c, I, r]) => s + I / (1 + ((du - a) ** 2 + (v - b) ** 2 + (w - c) ** 2) / (r * r)), 0);
const DHEAD = [DLAMP.du - 0.16, 1.06, DLAMP.w + 0.1];  // the desk lamp's head: its light goes down and to the left
const deskV = (du, v, w) => (v > DHEAD[1] ? 0 : 0.55 / (1 + ((du - DHEAD[0] + 0.2) ** 2 + (w - DHEAD[2]) ** 2 + ((v - DHEAD[1]) * 0.6) ** 2) / 0.14));
/** the room point of pixel (x, y) on the level plane v m above the floor: [du, w] */
function onLevel(x, y, v) {
  const Z = FOCAL * (v - EYE) / (HORIZON - y), X = (x - CX) / FOCAL * Z;
  return [(X - CORNER.X) * UX + (Z - CORNER.Z) * UZ, (X - CORNER.X) * UZ - (Z - CORNER.Z) * UX];
}
const RUG = { u0: -2.8, u1: -1.1, w0: 0.42, w1: 1.9 };
function paintShell(p) {
  for (let x = 0; x < W; x++) {
    const side = x >= CORNER_X, xc = x + 0.5;
    const yc = side ? yAtR(xc, CEIL) : yAt(xc, CEIL), yf = side ? yAtR(xc, 0) : yAt(xc, 0);
    const du = side ? 0 : duAt(xc), w = side ? wAtR(xc) : 0, Z = depth(du, w);
    for (let y = 0; y < H; y++) {
      const yy = y + 0.5, n = (vnoise(x / 6, y / 4, 3) - 0.5) * 0.05 + (vnoise(x / 23, y / 15, 5) - 0.5) * 0.05;
      if (yy < yc) {                                   // the ceiling: plaster, the floor lamp's circle of light on it
        const [cu, cw] = onLevel(xc, yy, CEIL);
        let v = 0.1 + 0.6 * lampV(cu, CEIL, cw) + 0.26 * smooth(1.1, 0.2, Math.hypot(cu - FLAMP.du, cw - FLAMP.w)) + n;
        if (yc - yy < 1.5) v -= 0.1;                   // the shadow line where it meets the wall
        p.set(x, y, pick(CEILP, v, x, y, 0.6));
      } else if (yy >= yf) {                           // the floor: boards running along the window wall, a rug under the chair
        const [fu, fw] = onLevel(xc, yy, 0), bi = Math.floor(fw / 0.11);
        let v = 0.32 + (hash(bi, 7) - 0.5) * 0.16 + 0.5 * lampV(fu, 0, fw) + n;
        if (frac(fw / 0.11) < 0.1 || frac((fu + hash(bi, 9) * 0.9) / 0.9) < 0.012) v -= 0.16;
        if (fu > RUG.u0 && fu < RUG.u1 && fw > RUG.w0 && fw < RUG.w1) {
          const e = Math.min(fu - RUG.u0, RUG.u1 - fu, fw - RUG.w0, RUG.w1 - fw);
          const base = e < 0.05 ? '#a88e6c' : e < 0.12 ? '#5a2418' : (Math.floor(fu / 0.09) + Math.floor(fw / 0.09)) % 2 ? '#7a3422' : '#6c2c1c';
          p.set(x, y, mixHex(base, '#0c0806', clamp(0.75 - 0.9 * lampV(fu, 0, fw), 0, 0.8)));
        } else p.set(x, y, pick(FLOORP, v, x, y, 0.6));
      } else {                                         // the two walls
        const vh = EYE + (HORIZON - yy) * Z / FOCAL;
        let v = 0.13 + 0.8 * lampV(du, vh, w) + (side ? 0 : deskV(du, vh, 0)) + n;
        if (!side && vh < DESK.top && du > DESK.u0 && du < DESK.u1) v *= 0.4;        // under the desk
        if (!side && x > CORNER_X - 3) v -= 0.08 * (x - CORNER_X + 3);               // the corner's own shadow
        if (side && x < CORNER_X + 2) v -= 0.1;
        if (vh < 0.09) p.set(x, y, pick(WOOD, 0.25 + 0.4 * lampV(du, vh, w) + (vh > 0.075 ? 0.2 : 0), x, y, 0.5));   // the skirting board
        else p.set(x, y, pick(PLASTER, v, x, y, 0.55));
      }
    }
  }
}
function paintWindow(p) {
  const { u0, u1, sill, head } = WIN, T = -WALL_T;
  const quad = (a, b, c, d, fn) => fillPoly([a, b, c, d], fn);
  // the reveal: the wall's own thickness round the opening, in the city's light
  quad(P(u0, head), P(u1, head), P(u1, head, T), P(u0, head, T), (x, y) => p.set(x, y, pick(COOLP, 0.2 + 0.25 * (x / W) + (bayer(x, y) - 0.5) * 0.1, x, y)));
  quad(P(u0, head), P(u0, head, T), P(u0, sill, T), P(u0, sill), (x, y) => p.set(x, y, pick(PLASTER, 0.3, x, y)));
  quad(P(u1, head, T), P(u1, head), P(u1, sill), P(u1, sill, T), (x, y) => {
    const f = (x - P(u1, head, T)[0]) / (P(u1, head)[0] - P(u1, head, T)[0]);       // 0 at the glass, 1 at the room
    p.set(x, y, f < 0.5 + (bayer(x, y) - 0.5) * 0.5 ? pick(COOLP, 0.75 - 0.3 * f, x, y) : pick(PLASTER, 0.42 + 0.2 * f, x, y));
  });
  // the timber frame on the glass's own plane: rails top and bottom, two stiles and two mullions; the glass is cleared
  const xa = UPRIGHTS[0][0], xb = UPRIGHTS[3][1];
  for (let x = xa; x < xb; x++) {
    const yt = Math.round(yAt(x + 0.5, head, T)), yb = Math.round(yAt(x + 0.5, sill, T)), up = UPRIGHTS.find(([a, b]) => x >= a && x < b);
    const warm = 0.2 + 0.34 * smooth(120, 345, x);     // the lamps are at the right
    for (let y = yt; y < yb; y++) {
      if (inGlass(x, y)) { p.clear(x, y); continue; }
      let v = warm + (vnoise(x / 2, y / 9, 31) - 0.5) * 0.08;
      if (up) v += x === up[0] ? 0.16 : x === up[1] - 1 ? -0.2 : x === up[0] + 1 ? 0.06 : 0;
      else if (y < glassTop(x)) v += y === yt ? -0.15 : y === glassTop(x) - 1 ? -0.22 : y === yt + 1 ? 0.1 : 0;
      else v += y === glassBot(x) ? 0.2 : y === yb - 1 ? -0.2 : y === glassBot(x) + 1 ? 0.08 : 0;
      p.set(x, y, pick(WOOD, v, x, y, 0.5));
    }
  }
  for (const q of PANES) for (let x = q.x0; x < q.x1; x++) p.tint(x, glassTop(x) - 1, '#05030a', 0.35);   // the rail's shadow line
  // the sill: an oak board that runs out into the room, its edge towards us
  const s0 = u0 - 0.06, s1 = u1 + 0.06, lip = 0.13;
  quad(P(s0, sill, T), P(s1, sill, T), P(s1, sill, lip), P(s0, sill, lip), (x, y) => {
    const du = duAt(x + 0.5, lip);
    p.set(x, y, pick(OAK, 0.34 + 0.5 * lampV(du, sill, lip) + deskV(du, sill, lip) + (vnoise(x / 9, y, 41) - 0.5) * 0.08, x, y, 0.5));
  });
  quad(P(s0, sill, lip), P(s1, sill, lip), P(s1, sill - 0.04, lip), P(s0, sill - 0.04, lip), (x, y) => {
    const du = duAt(x + 0.5, lip);
    p.set(x, y, pick(OAK, 0.14 + 0.42 * lampV(du, sill, lip) + 0.7 * deskV(du, sill, lip), x, y, 0.5));
  });
  // crisp edges where the opening cuts the wall
  line(p, ...P(u0, head), ...P(u1, head), PLASTER[4]);
  line(p, ...P(u1, head), ...P(u1, sill), PLASTER[9]);
  line(p, ...P(u0, head), ...P(u0, sill), PLASTER[3]);
}
/** a linen curtain hanging from the rail between du a and b, down to height `foot`; `fn(x, y, col)` takes its pixels */
function paintCurtain(a, b, foot, seed, fn, base = 0.2) {
  const RAIL_V = 2.335, wc = 0.09;
  const [xa] = P(a, RAIL_V, wc), [xb] = P(b, RAIL_V, wc);
  for (let x = Math.floor(xa); x <= Math.ceil(xb); x++) {
    const du = duAt(x + 0.5, wc), Z = depth(du, wc);
    if (du < a || du > b) continue;
    const y0 = Math.round(yAt(x + 0.5, RAIL_V, wc)), y1 = Math.round(yAt(x + 0.5, foot, wc));
    for (let y = y0; y < y1; y++) {
      const vh = EYE + (HORIZON - y) * Z / FOCAL, drift = 0.5 * Math.sin(vh * 2.4 + seed);
      const fold = Math.sin((du - a) / (b - a) * 9.4 + drift + seed), hem = y1 - y;
      if (hem < 2 && fold < -0.3) continue;            // a scalloped hem
      let v = base + 0.6 * lampV(du, vh, wc) + 0.18 * fold + (fold > 0.75 ? 0.08 : 0);
      if (y - y0 < 3) v -= 0.12;                       // the gathered heading under the rail
      fn(x, y, pick(LINEN, v, x, y, 0.5));
    }
  }
}
function paintRail(p) {
  const RAIL_V = 2.335, wc = 0.1, a = -4.4, b = -0.03;
  for (let x = 0; x <= CORNER_X; x++) {
    const du = duAt(x + 0.5, wc);
    if (du < a || du > b) continue;
    const y = Math.round(yAt(x + 0.5, RAIL_V, wc)), lv = 0.3 + 0.5 * lampV(du, RAIL_V, wc);
    p.set(x, y - 1, pick(WOOD, lv + 0.25, x, y)); p.set(x, y, pick(WOOD, lv - 0.05, x, y));
    p.tint(x, y + 1, '#080406', 0.3);
  }
  const [bx, by] = P(b, RAIL_V, wc);
  p.fill(Math.round(bx), Math.round(by) - 2, 2, 4, WOOD[6]);
}

/* ================= Furniture (painted into the room) ================= */
/** on the upright plane du m from the side wall and parallel to it: w of screen column x, and the row of height v there */
const wOnU = (x, du) => { const k = (x - CX) / FOCAL; return (k * (CORNER.Z + du * UZ) - CORNER.X - du * UX) / (UZ + k * UX); };
const yOnU = (x, v, du) => HORIZON - FOCAL * (v - EYE) / depth(du, wOnU(x, du));
const R = pt => pt.map(Math.round);
function paintDesk(p) {
  const { u0, u1, top, w0, w1 } = DESK, apron = 0.09;
  const front = (a, b, va, vb, fn) => fillPoly([P(a, va, w1), P(b, va, w1), P(b, vb, w1), P(a, vb, w1)], fn);
  for (const lu of [u0 + 0.07, -2.72, u1 - 0.07]) front(lu - 0.03, lu + 0.03, top - apron, 0, (x, y) => p.set(x, y, pick(OAK, 0.1 + 0.3 * lampV(lu, 0.4, w1) + (x & 1) * 0.03, x, y, 0.5)));
  front(-1.42, u1 - 0.03, top - apron, 0.14, (x, y) => {                    // drawers under its right end
    const du = duAt(x + 0.5, w1), vh = EYE + (HORIZON - y) * depth(du, w1) / FOCAL, seam = frac((top - apron - vh) / 0.165) < 0.07;
    p.set(x, y, pick(OAK, (seam ? 0.05 : 0.17) + 0.34 * lampV(du, vh, w1), x, y, 0.5));
  });
  for (let k = 0; k < 3; k++) { const [kx, ky] = R(P(-1.06, top - apron - 0.08 - k * 0.165, w1)); p.fill(kx, ky, 2, 1, '#d8b060'); }
  front(u0, u1, top, top - apron, (x, y) => {
    const du = duAt(x + 0.5, w1);
    p.set(x, y, pick(OAK, 0.14 + 0.32 * lampV(du, top, w1) + 0.5 * deskV(du, top, w1) + (vnoise(x / 11, y / 2, 51) - 0.5) * 0.06, x, y, 0.5));
  });
  fillPoly([P(u0, top, w0), P(u1, top, w0), P(u1, top, w1), P(u0, top, w1)], (x, y) => {
    const [du, w] = onLevel(x + 0.5, y + 0.5, top);
    p.set(x, y, pick(OAK, 0.3 + 0.42 * lampV(du, top, w) + 1.15 * deskV(du, top, w) + (vnoise(x / 13, y / 1.5, 53) - 0.5) * 0.08, x, y, 0.5));
  });
  line(p, ...R(P(u0, top, w1)), ...R(P(u1, top, w1)), OAK[7]);
}
const BOOKS = ['#6e2a22', '#2a4254', '#b8a47e', '#34503f', '#94502c', '#522a36', '#a8803a', '#35354a', '#7e6e50', '#8a3a2e'];
function paintDeskThings(p) {
  const top = DESK.top;
  // a small mushroom lamp at the far end
  let [x, y] = R(P(MUSH.du, top, MUSH.w));
  p.fill(x - 3, y - 1, 7, 2, '#5a3a22'); p.fill(x - 1, y - 9, 2, 8, '#c89a5a');
  for (let j = 0; j < 8; j++) { const hw = Math.round(7 * Math.sqrt(1 - ((7 - j) / 8) ** 2)); for (let i = -hw; i <= hw; i++) p.set(x + i, y - 17 + j, j === 7 ? '#c87a3a' : pick(['#f0a850', '#ffc878', '#ffe2a8', '#fff2d0'], 0.9 - Math.abs(i) / 9 - (7 - j) / 14, x + i, y + j)); }
  // books lying flat, a paperback on top
  [x, y] = R(P(-3.02, top, 0.3));
  [[26, '#2a4254', '#3d5a70'], [24, '#8a3a2e', '#a85440'], [27, '#b8a47e', '#d4c49a'], [20, '#34503f', '#4a6c56']].forEach(([w, c, hi], k) => {
    const ox = [0, 2, -1, 3][k];
    p.fill(x - 13 + ox, y - 4 - k * 4, w, 4, c); p.fill(x - 13 + ox, y - 4 - k * 4, w, 1, hi); p.fill(x - 13 + ox + w - 2, y - 3 - k * 4, 2, 3, '#e8dcc0');
  });
  // a pilea in a terracotta pot, in front of the mullion
  [x, y] = R(P(-2.86, top, 0.34));
  for (let j = 0; j < 8; j++) for (let i = -4 + (j > 5 ? 1 : 0); i <= 4 - (j > 5 ? 1 : 0); i++) p.set(x + i, y - 8 + j, j === 0 ? '#c8744a' : i > 1 ? '#b05e36' : i < -2 ? '#6e3620' : '#8a4628');
  const r = rng(77);
  for (let k = 0; k < 13; k++) {
    const a = -Math.PI * (0.08 + 0.84 * r()), len = 6 + r() * 11, lx = Math.round(x + Math.cos(a) * len), ly = Math.round(y - 9 + Math.sin(a) * len), s = r() < 0.5 ? 2 : 3;
    line(p, x, y - 9, lx, ly, '#2a4a26');
    for (let j = -s; j <= s; j++) for (let i = -s; i <= s; i++) if (i * i + j * j <= s * s + 1) p.set(lx + i, ly + j, LEAF[clamp(3 + (i > 0 ? 1 : 0) - (j > 0 ? 1 : 0) + (lx > x ? 1 : 0), 1, 6)]);
    p.set(lx, ly, LEAF[2]);
  }
  // an open notebook where the lamp's light falls, a pen across it
  fillPoly([P(-2.2, top, 0.24), P(-1.84, top, 0.24), P(-1.84, top, 0.5), P(-2.2, top, 0.5)], (i, j) => {
    const [du] = onLevel(i + 0.5, j + 0.5, top), gut = Math.abs(du + 2.02) < 0.012;
    p.set(i, j, gut ? '#b09a74' : (j & 1) && frac(du * 40) < 0.8 ? '#e2d2ac' : '#f6ead0');
  });
  // the desk lamp: a weighted foot, a brass arm, an enamel shade turned down and to the left
  const [bx, by] = R(P(DLAMP.du, top, DLAMP.w)), [ex, ey] = R(P(DLAMP.du + 0.02, 1.0, DLAMP.w)), [hx, hy] = R(P(...DHEAD));
  p.fill(bx - 4, by - 1, 9, 2, '#241c18'); p.fill(bx - 3, by - 2, 7, 1, '#5a4a3a');
  line(p, bx, by - 3, ex, ey, '#b88c3c'); line(p, bx + 1, by - 3, ex + 1, ey, '#5a4018');
  line(p, ex, ey, hx + 4, hy - 3, '#b88c3c'); line(p, ex, ey + 1, hx + 4, hy - 2, '#5a4018');
  p.fill(ex - 1, ey - 1, 3, 3, '#e0bc68');
  for (let j = 0; j < 7; j++) { const hw = 2 + j; for (let i = -hw; i <= hw - 1; i++) p.set(hx + i + 1, hy - 4 + j, j === 6 ? '#fff4d0' : i >= hw - 2 ? '#f4ecd8' : i <= -hw + 1 ? '#6a6458' : '#c8c0aa'); }
  p.fill(hx - 2, hy + 3, 5, 1, '#ffe8a8');
  // a cup of pens beyond it
  [x, y] = R(P(-0.98, top, 0.22));
  p.fill(x - 2, y - 7, 5, 7, '#3a5a64'); p.fill(x - 2, y - 7, 1, 7, '#2a4048'); p.fill(x + 2, y - 7, 1, 7, '#5a8a94');
  for (const [i, h, c] of [[-1, 5, '#d8b060'], [0, 7, '#c84a3a'], [1, 4, '#e8e0d0']]) p.fill(x + i, y - 7 - h, 1, h, c);
}
/** the side wall: a shelf of books with a trailing pothos, a clock, a print, the sideboard with the record player */
function paintSideWall(p) {
  const sv = SHELF.v, du = -0.2;
  // books standing on the shelf (their spines on the plane du = -0.17), then the plank's front edge
  const r = rng(91);
  for (let w = SHELF.w0 + 0.05; w < SHELF.w1 - 0.3;) {
    const bw = 0.028 + r() * 0.03, bh = 0.17 + r() * 0.1, col = BOOKS[Math.floor(r() * BOOKS.length)], lean = r() < 0.12;
    const [xa] = P(-0.17, sv, w), [xb] = P(-0.17, sv, w + bw);
    for (let x = Math.round(xa); x < Math.round(xb); x++) {
      const y1 = Math.round(yOnU(x + 0.5, sv, -0.17)), y0 = Math.round(yOnU(x + 0.5, sv + bh, -0.17)), lv = 0.3 + 0.7 * lampV(-0.17, sv + 0.1, wOnU(x + 0.5, -0.17));
      for (let y = y0 + (lean ? 2 : 0); y < y1; y++) p.set(x, y, mixHex(mixHex(col, '#0c0806', clamp(1 - lv, 0, 0.75)), '#ffd8a0', x === Math.round(xa) ? 0.2 : 0));
    }
    w += bw + (r() < 0.15 ? 0.05 : 0.004);
  }
  for (let x = Math.round(P(du, sv, SHELF.w0)[0]); x < W; x++) {
    if (wOnU(x + 0.5, du) > SHELF.w1) break;
    const y = Math.round(yOnU(x + 0.5, sv, du)), lv = 0.3 + 0.5 * lampV(du, sv, wOnU(x + 0.5, du));
    p.set(x, y, pick(OAK, lv + 0.2, x, y)); p.set(x, y + 1, pick(OAK, lv, x, y)); p.set(x, y + 2, pick(OAK, lv - 0.2, x, y));
    p.tint(x, y + 3, '#080406', 0.35); p.tint(x, y + 4, '#080406', 0.18);
  }
  // the pothos: a pot at the near end of the shelf, vines hanging down the wall
  const [px, py] = R(P(-0.14, sv, SHELF.w1 - 0.16));
  for (let k = 0; k < 5; k++) {
    const vx = px - 7 + k * 4, len = [34, 22, 44, 28, 16][k];
    for (let j = 0; j < len; j++) {
      const x = vx + Math.round(Math.sin(j * 0.21 + k * 1.7) * 1.6), y = py + 2 + j;
      p.set(x, y, '#24421e');
      if (j % 5 === 2) { const s = (j / 5 + k) & 1 ? -1 : 1; for (const [i, jj, c] of [[s, 0, 4], [s * 2, 0, 5], [s, 1, 3], [s * 2, 1, 4], [s * 3, 1, 3], [s * 2, 2, 2]]) p.set(x + i, y + jj, LEAF[c]); }
    }
  }
  for (let j = 0; j < 7; j++) for (let i = -5; i <= 5; i++) if (Math.abs(i) <= 5 - (j > 4 ? j - 4 : 0)) p.set(px + i, py - 7 + j, j === 0 ? '#e8e0d0' : i > 2 ? '#c8c0b0' : i < -3 ? '#6a6458' : '#a8a090');
  for (let k = 0; k < 9; k++) { const lx = px - 6 + (k * 5) % 13, ly = py - 9 - (k * 3) % 5; for (const [i, j, c] of [[0, 0, 4], [1, 0, 5], [0, 1, 3], [1, 1, 4], [2, 1, 3]]) p.set(lx + i, ly + j, LEAF[c]); }
  // the clock (its hands are drawn each frame)
  const [cx, cy] = CLOCK;
  for (let j = -9; j <= 9; j++) for (let i = -8; i <= 8; i++) {
    const e = (i / 7.6) ** 2 + (j / 8.8) ** 2;
    if (e <= 1) p.set(cx + i, cy + j, e > 0.74 ? (i + j < 0 ? '#5a3a20' : '#2e1c10') : e > 0.6 ? '#b8a888' : '#efe4c8');
  }
  for (let k = 0; k < 12; k++) p.set(cx + Math.round(Math.sin(k * TAU / 12) * 5), cy - Math.round(Math.cos(k * TAU / 12) * 6), '#6a5a48');
  // a framed print: three plain shapes on cream paper
  const q = (w, v) => P(0, v, w), F = [q(0.5, 1.4), q(1.02, 1.4), q(1.02, 0.96), q(0.5, 0.96)];
  fillPoly(F, (x, y) => {
    const w = wAtR(x + 0.5), vh = EYE + (HORIZON - y) * depth(0, w) / FOCAL, a = (w - 0.5) / 0.52, b = (1.4 - vh) / 0.44, lv = clamp(0.35 + 0.8 * lampV(0, vh, w), 0, 1);
    let col = '#e8dcc0';
    if (a < 0.07 || a > 0.93 || b < 0.08 || b > 0.92) col = '#3a2414';
    else if (Math.hypot(a - 0.38, b - 0.4) < 0.17) col = '#d8a83a';
    else if (a > 0.42 && a < 0.8 && b > 0.36 && b < 0.8) col = '#2f6068';
    else if (a > 0.2 && a < 0.5 && b > 0.62 && b < 0.72) col = '#b8502c';
    p.set(x, y, mixHex(col, '#0c0806', 0.7 * (1 - lv)));
  });
  // the sideboard: its top, then its front with two doors
  const { w0, top, deep } = SIDE;
  for (let x = Math.round(P(-deep, top, w0)[0]); x < W; x++) {
    const yt = Math.round(yOnU(x + 0.5, top, -deep)), w = wOnU(x + 0.5, -deep);
    for (let y = yt; y < H; y++) {
      const vh = EYE + (HORIZON - y) * depth(-deep, w) / FOCAL, seam = Math.abs(frac(w / 0.52) - 0.5) > 0.485 || vh < 0.1;
      p.set(x, y, pick(OAK, (seam ? 0.06 : 0.2) + 0.3 * lampV(-deep, vh, w) + (vnoise(x / 3, y / 14, 61) - 0.5) * 0.06, x, y, 0.5));
    }
  }
  fillPoly([P(0, top, w0), P(0, top, 2), P(-deep, top, 2), P(-deep, top, w0)], (x, y) => {
    const [fu, fw] = onLevel(x + 0.5, y + 0.5, top);
    p.set(x, y, pick(OAK, 0.3 + 0.5 * lampV(fu, top, fw), x, y, 0.5));
  });
  // the record player: a low plinth, the platter (the record turns each frame), a few sleeves leaning on the wall
  const [tx, ty] = TURNTABLE;
  p.fill(tx - 19, ty - 2, 38, 5, '#2c2420'); p.fill(tx - 19, ty - 2, 38, 1, '#5a4c40'); p.fill(tx - 19, ty + 2, 38, 1, '#14100e');
  for (let i = -14; i <= 14; i++) { const hh = Math.round(2.4 * Math.sqrt(1 - (i / 14.5) ** 2)); for (let j = -hh; j <= hh; j++) p.set(tx + i, ty - 3 + j, Math.abs(i) < 4 && j === 0 ? '#d86a3a' : (i + j * 3) % 5 === 0 ? '#2a2630' : '#16141c'); }
  [['#b8a47e', 15], ['#2a5560', 17], ['#8a3a2e', 16]].forEach(([c, h], k) => { const sx = tx + 24 + k * 3; if (sx < W - 2) { p.fill(sx, ty - h, 3, h - 1, c); p.fill(sx, ty - h, 1, h - 1, mixHex(c, '#ffffff', 0.2)); } });
}
const CLOCK = R(P(0, 1.96, 0.66)), TURNTABLE = R(P(-0.21, SIDE.top + 0.03, 0.92));
function paintFloorLamp(p) {
  const [bx, by] = R(P(FLAMP.du, 0, FLAMP.w)), [, y1] = R(P(FLAMP.du, 1.42, FLAMP.w)), [, y0] = R(P(FLAMP.du, 1.73, FLAMP.w));
  p.fill(bx - 6, by - 1, 13, 2, '#1c1410');
  for (let y = y1; y < by; y++) { p.set(bx, y, '#8a6a34'); p.set(bx + 1, y, '#3a2a14'); }
  for (let y = y0; y <= y1; y++) {                     // a linen drum, glowing
    const k = (y - y0) / (y1 - y0), hw = Math.round(lerp(11, 14.5, k));
    for (let i = -hw; i <= hw; i++) {
      const u = Math.abs(i) / hw;
      let v = 1 - 0.55 * u * u - 0.25 * Math.abs(k - 0.55);
      if (y === y0 || y === y1) v -= 0.4;
      p.set(bx + i, y, pick(['#8a4a1a', '#c8742a', '#f0a044', '#ffc468', '#ffdf98', '#fff2c8'], v, bx + i, y, 0.6));
    }
  }
}
const FLAMP_AT = [Math.round(P(FLAMP.du, 1.57, FLAMP.w)[0]), Math.round(P(FLAMP.du, 1.57, FLAMP.w)[1])];
const SHADE = [Math.round(P(FLAMP.du, 1.73, FLAMP.w)[1]), Math.round(P(FLAMP.du, 1.42, FLAMP.w)[1])];   // its shade's top and bottom rows
/** the near corner of a bed at the bottom left: a folded knit blanket, mostly in shadow */
function paintBed(p) {
  const top = x => 240 + x * 0.05 + (x > 150 ? (x - 150) * (x - 150) * 0.02 : 0);
  for (let x = 0; x < 190; x++) for (let y = Math.round(top(x)); y < H; y++) {
    const d = y - top(x), fold = 0.5 + 0.5 * Math.sin((x * 0.6 + y * 1.3) / 11 + vnoise(x / 30, y / 20, 81) * 3);
    const lv = clamp(0.42 - d * 0.01 - x * 0.001 + 0.12 * fold, 0.06, 0.6), knit = ((x >> 1) + y) % 3 === 0 ? 0.07 : 0;
    p.set(x, y, mixHex(d < 1.5 ? '#c08a60' : '#8a3e2a', '#0a0605', clamp(1 - lv - knit, 0, 1)));
  }
}

/* ================= Whoever sits at the desk (seen from behind, and a little from the left) ================= */
const CROWD = 1;                                     // the room's own people: the sitter (rank 0), the neighbours across the street
const SZ = depth(SEAT.du, SEAT.w), SX = Math.round(P(SEAT.du, 0, SEAT.w)[0]);   // their distance, their column
const sy = v => Math.round(HORIZON - FOCAL * (v - EYE) / SZ);                  // the row of a height at that distance
const HAIR = ['#0e0a0c', '#1a1214', '#2a1c1c', '#3e2a24', '#5a3c2c'];
function renderChair() {
  const p = pix(W, H), x0 = SX - 22, x1 = SX + 22, yT = sy(0.9), yM = sy(0.67), ySeat = sy(0.46);
  const lv = x => 0.2 + 0.36 * smooth(x0, x1, x);     // the lamp is on its right
  const wood = (x, y, d) => p.set(x, y, pick(OAK, lv(x) + d, x, y, 0.5));
  for (const px of [x0, x1 - 2]) for (let y = yT + 2; y < H; y++) for (let i = 0; i < 3; i++) wood(px + i, y, i === 0 ? 0.1 : i === 2 ? -0.1 : 0);
  for (let x = x0; x <= x1; x++) {
    const bow = Math.round(1.6 * Math.cos((x - SX) / 22 * Math.PI / 2));
    for (let j = 0; j < 6; j++) wood(x, yT - bow + j, j === 0 ? 0.2 : j === 5 ? -0.14 : 0);
    for (let j = 0; j < 4; j++) wood(x, yM + j, j === 0 ? 0.14 : j === 3 ? -0.14 : -0.03);
    for (let j = 0; j < 5; j++) wood(x, ySeat + j, j === 0 ? 0.12 : -0.05 * j);
  }
  return trim(p);
}
function renderBody() {
  const p = pix(W, H), yS = sy(1.0), yB = sy(0.47);
  for (let y = yS - 4; y < yB; y++) {
    const k = (y - yS) / (yB - yS);
    const hw = Math.round(y < yS ? lerp(7, 19, (y - yS + 4) / 4) : y < yS + 5 ? lerp(19, 22, (y - yS) / 5) : lerp(22, 18, smooth(0.08, 0.6, k)));
    for (let dx = -hw; dx <= hw; dx++) {
      const u = dx / hw;
      let col = pick(NAVY, 0.42 - 0.2 * k + 0.12 * (1 - u * u) + 0.1 * u + ((y & 3) === 0 ? -0.04 : 0), SX + dx, y, 0.7);   // a knit, in rows
      if (dx >= hw - 1) col = dx === hw ? '#d89a62' : '#7a5a4c';        // the lamp's light down the right edge
      else if (dx <= -hw + 1) col = '#4a4674';                          // the window's down the left
      p.set(SX + dx, y, col);
    }
  }
  for (let dx = -11; dx <= 11; dx++) {                // the hood, folded on the upper back
    const y = yS + 2 + Math.round(5 * (1 - (dx / 11) ** 2));
    p.set(SX + dx, y, '#0e1320'); p.set(SX + dx, y - 1, NAVY[4]);
  }
  for (let y = sy(1.1); y < yS - 2; y++) for (let dx = -3; dx <= 3; dx++) p.set(SX + dx, y, dx > 2 ? '#a8744e' : dx < -1 ? '#4a302a' : '#6e4a3a');   // the neck
  return trim(p);
}
/** the back of a head: a bob cut straight at the nape, headphones over it (25 x 28, the crown on row 2) */
function renderHead() {
  const w = 25, h = 28, cx = 12, p = pix(w, h);
  for (let y = 2; y < 26; y++) for (let x = 0; x < w; x++) {
    const dx = x - cx, dy = y - 12.5, hw = y < 19 ? 8.8 * Math.sqrt(Math.max(0, 1 - (dy / 11) ** 2)) : lerp(8.1, 8.6, (y - 19) / 6);
    if (Math.abs(dx) > hw + 0.2 || (y === 25 && (x & 1))) continue;
    let col = pick(HAIR, 0.3 + 0.2 * dx / 8.8 + 0.18 * Math.max(0, 1 - Math.hypot(dx - 2, dy + 5) / 6) - (hash(x, y >> 2, 3) < 0.25 ? 0.09 : 0), x, y, 0.8);
    if (dx > hw - 1.2) col = '#b07a4a'; else if (dx < -hw + 1) col = '#4c4674';
    p.set(x, y, col);
  }
  for (let dx = -10; dx <= 10; dx++) {                // the band over the crown
    const y = Math.round(13 - 12.6 * Math.sqrt(Math.max(0, 1 - (dx / 10.4) ** 2)));
    p.set(cx + dx, y, dx > 2 ? '#f4ead2' : dx < -5 ? '#8c8474' : '#cfc4aa');
    if (Math.abs(dx) > 8) p.set(cx + dx, y + 1, dx > 0 ? '#d8ccb4' : '#8c8474');
  }
  for (let j = 0; j < 9; j++) for (let i = 0; i < 4; i++) {   // a cup over the near ear
    if ((j === 0 || j === 8) && (i === 0 || i === 3)) continue;
    p.set(cx - 12 + i, 10 + j, i === 3 ? '#2a221e' : i === 0 ? '#7a7264' : j < 2 ? '#e8dcc4' : '#b8ad96');
  }
  for (let j = 0; j < 7; j++) { p.set(cx + 10, 11 + j, '#f6ecd6'); if (j > 0 && j < 6) p.set(cx + 11, 11 + j, '#d8ccb4'); }   // a sliver of the far one
  return p.done();
}
// The near arm, from the shoulder: [elbow, fist] for each thing it does. Reaching, the fist closes on the standing mug's
// handle. Then the mug comes in to the chest and up to the lips on the far side of the body: seen from behind, the back
// and the head hide it all the way, and the sip is an elbow coming up and a head tipping back.
const MUG_AT = R(P(-2.42, DESK.top, 0.36));          // where the mug stands (the middle of its foot)
const SHY = sy(1.0), GRIP = [MUG_AT[0] + 4, MUG_AT[1] - 6];   // the shoulders' row; the middle of the mug's handle
const ARM = {
  sh: [SX - 20, SHY + 2],
  write: [[SX - 34, SHY + 29], [SX - 22, SHY + 26]],   // the elbow out and low, the forearm in over the desk to the page: the hand
                                                     // is in front of the chest, half behind the body from here
  reach: [[GRIP[0] + 15, GRIP[1] - 5], GRIP],
  near: [[SX - 31, SHY + 24], [SX - 13, SHY + 19]],  // the mug brought in to the chest: from here on the body hides it
  drink: [[SX - 29, SHY + 7], [SX - 5, SHY - 12]],   // at the lips: the forearm shows as far as the hair
};
const PEN = [ARM.write[1][0] - 3, ARM.write[1][1] + 2], PEN_C = ['#2c3046', '#e8e0d0'];   // where the pen waits on the page; its barrel and cap
function limb(x0, y0, x1, y1, r, col) {
  const n = Math.max(1, Math.ceil(Math.hypot(x1 - x0, y1 - y0) / 1.5));
  ctx.fillStyle = col;
  for (let k = 0; k <= n; k++) {
    const x = Math.round(lerp(x0, x1, k / n)), y = Math.round(lerp(y0, y1, k / n));
    ctx.fillRect(x - r, y - r + 1, 2 * r + 1, 2 * r - 1); ctx.fillRect(x - r + 1, y - r, 2 * r - 1, 2 * r + 1);
  }
}
const pose = (a, b, f) => [[lerp(a[0][0], b[0][0], f), lerp(a[0][1], b[0][1], f)], [lerp(a[1][0], b[1][0], f), lerp(a[1][1], b[1][1], f)]];
/** what the sitter is doing at t. A sip (SIP_DUR s): out to the mug (`reach` 0..1), a beat to close the fingers on its
 *  handle (`held`), in to the chest (`pull`), up to the lips out of sight (`raise`) with a tip of the mug (`tip`), and
 *  the same way back. Also the elbow, the fist's middle and the head's drop in px. */
function sitterAt(t) {
  let up = 0, reach = 0, pull = 0, raise = 0, tip = 0, held = false;
  for (const [a, b] of LOOK_UP) up = Math.max(up, env(t, a, b, 1.4));
  for (const s of SIP) {
    const tau = t - s;
    if (tau < 0 || tau >= SIP_DUR) continue;
    reach = smooth(0, 0.9, tau) * (1 - smooth(4.3, 5, tau));
    pull = smooth(1.15, 1.6, tau) * (1 - smooth(3.7, 4.15, tau));
    raise = smooth(1.6, 2.2, tau) * (1 - smooth(3.2, 3.7, tau));
    tip = smooth(2.4, 2.6, tau) * (1 - smooth(2.9, 3.1, tau));
    held = tau >= 1 && tau < 4.25;
  }
  const [el, fist] = raise > 0 ? pose(ARM.near, ARM.drink, raise) : pull > 0 ? pose(ARM.reach, ARM.near, pull) : pose(ARM.write, ARM.reach, reach);
  if (raise > 0) el[1] -= tip;                         // the elbow comes up a little more as the mug tips
  else fist[1] -= 2 * Math.sin(Math.PI * (pull > 0 ? pull : reach));   // the hand clears the desk on its way, and carries the mug over it
  const writing = reach === 0;
  if (writing && up < 0.5 && frac(t / 8) < 0.62) fist[0] += Math.round(wave(t, 600));   // the pen moves, in bursts
  return { el, fist: R(fist), writing, reach, pull, held, away: raise > 0, head: Math.round(lerp(2 - 2 * up, -tip, raise)) };
}
function drawSitter(t, crowd) {
  if (crowd <= 0) { blit(CHAIR_C); ctx.drawImage(MUG_C, MUG_AT[0] - 4, MUG_AT[1] - 9); drawSteam(t, MUG_AT[0], MUG_AT[1] - 9, 1); return; }
  const s = sitterAt(t), [sx0, sy0] = ARM.sh, [ex, ey] = s.el, [hx, hy] = s.fist;
  const lower = () => {                              // the forearm, the hand and what it holds
    if (!s.held) ctx.drawImage(MUG_C, MUG_AT[0] - 4, MUG_AT[1] - 9);
    else if (!s.away) ctx.drawImage(MUG_C, hx - 8, hy - 3);                // in the fist, by its handle
    if (!s.writing) { ctx.fillStyle = PEN_C[0]; ctx.fillRect(PEN[0] - 2, PEN[1], 4, 1); ctx.fillStyle = PEN_C[1]; ctx.fillRect(PEN[0] - 3, PEN[1], 1, 1); }   // the pen waits on the page
    const d = Math.hypot(hx - ex, hy - ey) || 1, wx = hx - 2.5 * (hx - ex) / d, wy = hy - 2.5 * (hy - ey) / d;   // the sleeve ends short of the fist
    limb(ex, ey, wx, wy, 2, NAVY[2]); limb(ex, ey - 1, wx, wy - 1, 1, NAVY[3]);
    limb(wx, wy, wx, wy, 2, NAVY[4]);                // the cuff
    if (s.away) return;
    const hand = s.held ? HAND_C.grip : s.writing ? HAND_C.fist : HAND_C.open;
    ctx.drawImage(hand, hx + (hand === HAND_C.open ? 4 : 2) - hand.width, hy - 2);   // open, the fingertips only just reach the handle
    if (s.writing) {                                 // the pen in the fist: only its back end shows over the hand, the point is beyond it
      ctx.fillStyle = PEN_C[1]; ctx.fillRect(hx - 1, hy - 5, 1, 2);
      ctx.fillStyle = PEN_C[0]; ctx.fillRect(hx - 1, hy - 3, 1, 1);
    }
  };
  const torso = () => {
    blit(BODY_C);
    ctx.drawImage(HEAD_C, SX - 11, sy(1.27) - 2 + s.head);
    limb(sx0, sy0, ex, ey, 3, NAVY[2]); limb(sx0 - 1, sy0, ex - 1, ey - 1, 1, NAVY[4]);   // the upper arm, on this side
  };
  // out over the desk the whole arm shows. Coming in front of the chest, what is past the elbow is on the far side of
  // the body: drawn first, so the back and the head cover the forearm, the fist and the mug
  if (s.pull > 0) { lower(); torso(); } else { torso(); lower(); }
  blit(CHAIR_C);
  if (!s.held) drawSteam(t, MUG_AT[0], MUG_AT[1] - 9, 1 - smooth(0.6, 1, s.reach));
}
// a hand is a few pixels: lamp-lit knuckles on the right, the shade under it. `open` reaches to the left for the handle;
// `grip` is closed on it, the thumb over the top
const SKIN_KEY = { S: '#c08a62', L: '#dca97c', D: '#8a5e46', K: '#6a4636' };
const FIST_ROWS = ['.SL.', 'SSSL', 'SSSL', '.DD.'];
const OPEN_ROWS = ['..SSL.', 'SSSSSL', 'SSSSSL', '.DDDD.'];
const GRIP_ROWS = ['.SSL', 'SSSL', 'KSSL', 'SSSL', '.DD.'];
const MUG_ROWS = ['.wwwwww..', 'wttttttw.', 'wwwwwwwhh', 'bbbbbbb.h', 'bbbbbbb.h', 'wwwwwwwh.', 'wwwwwww..', 'sswwwss..', '.sssss...'];
const MUG_KEY = { w: '#efe6d2', t: '#b8742c', b: '#3e7a84', h: '#cfc4ae', s: '#a89c86' };
function drawSteam(t, x, y, a) {
  if (a <= 0) return;
  for (let k = 0; k < 9; k++) {
    const u = frac(t * 30 / LOOP + k / 9), r = u < 0.35 ? 1 : 2;
    ctx.globalAlpha = a * 0.5 * Math.min(1, u * 5) * Math.pow(1 - u, 1.2);
    ctx.drawImage(PUFF[r], Math.round(x - 1 + (k % 3) + wave(t, 23, k * 0.17) * 2 * u + Math.sin(u * 6 + k) * 1.3 * u) - r, Math.round(y - 1 - u * 24) - r);
  }
  ctx.globalAlpha = 1;
}

/* ================= The cat on the sill, watching the rain (from behind; its head turns after a drop now and then) ================= */
const GINGER = ['#3a2010', '#5a3216', '#7e4a1e', '#a2662a', '#c4843a', '#dea458', '#f0c480'];
const CATX = 108, CATY = Math.round(yAt(108, WIN.sill, 0.06));   // the middle of its seat, on the sill board
function renderCat(turn) {
  const w = 25, h = 31, cx = 12, p = pix(w, h), hx = cx + turn, RIM = '#4a3a5a';
  const fur = (x, y, v) => p.set(x, y, pick(GINGER, v, x, y, 0.7));
  for (let y = 12; y < h; y++) {                       // the body: narrow at the neck, round at the haunches, lit from the right
    const k = (y - 12) / 18, hw = (k < 0.55 ? lerp(4.6, 10.2, Math.sin(k / 0.55 * Math.PI / 2)) : 10.2 + 0.4 * Math.sin((k - 0.55) / 0.45 * Math.PI)) - (y > h - 3 ? (y - h + 3) * 1.2 : 0);
    for (let dx = -Math.ceil(hw); dx <= Math.ceil(hw); dx++) {
      if (Math.abs(dx) > hw + 0.3) continue;
      const u = dx / hw, au = Math.abs(u);
      const stripe = y > 15 && y < h - 4 && (y + Math.round(au * 3)) % 5 === 0 && au > 0.25 && au < 0.82;   // short stripes off the spine
      if (dx <= -hw + 0.9) p.set(cx + dx, y, RIM);     // the city's light down its left side
      else fur(cx + dx, y, 0.44 + 0.28 * u - 0.1 * k - (stripe ? 0.2 : 0) - (y < 14 ? 0.14 : 0) - (dx === 0 && y > 14 ? 0.05 : 0));
    }
  }
  for (let y = 0; y < 13; y++) for (let x = 0; x < w; x++) {   // the head, wider than the neck, and two ears
    const dx = x - hx, e = (dx / 7.4) ** 2 + ((y - 7.5) / 5.3) ** 2;
    const earL = y < 5 && Math.abs(dx + 5 - turn) <= 0.4 + y * 0.6, earR = y < 5 && Math.abs(dx - 5 - turn) <= 0.4 + y * (turn ? 0.4 : 0.6);
    if (e > 1 && !earL && !earR) continue;
    if (dx < -6.3 || (earL && e > 1 && dx + 5 - turn < -y * 0.6 + 0.6)) { p.set(x, y, RIM); continue; }
    const mark = y > 2 && y < 6 && (dx === -2 || dx === 2 || dx === 0);     // the tabby's M, from behind
    fur(x, y, 0.5 + 0.26 * dx / 7.4 - (mark ? 0.18 : 0) - ((earL || earR) && e > 1 ? 0.1 : 0));
  }
  if (turn) for (const [i, j, c] of [[7, 8, '#f6ecd8'], [7, 9, '#f6ecd8'], [8, 8, '#e8a0a0'], [8, 9, '#f6ecd8'], [6, 6, '#2a1a10']]) p.set(hx + i, j, c);   // its muzzle and an eye, in profile
  return p.done();
}
function drawCat(t) {
  const turned = CAT_TURN.some(([a, b]) => t >= a && t < b) ? 1 : 0, x = CATX - 12, y = CATY - 30;
  // its tail lies along the sill to the right; the tip sways, and flicks up now and then
  let lift = 0.5 + 0.5 * wave(t, 40, 0.1);
  for (const f of TAIL_FL) { const e = t - f; if (e >= 0 && e < 1.6) lift = Math.max(lift, 3 * Math.abs(Math.sin(e / 1.6 * Math.PI * 2))); }
  for (let j = 0; j < 15; j++) {
    const k = j / 14, tx = CATX + 7 + j, ty = CATY - 2 - Math.round(lift * k * k * k);
    ctx.fillStyle = j % 4 === 3 ? GINGER[2] : j > 12 ? GINGER[6] : GINGER[4]; ctx.fillRect(tx, ty, 1, 2);
    ctx.fillStyle = GINGER[1]; ctx.fillRect(tx, ty + 2, 1, 1);
  }
  ctx.drawImage(CAT_C[turned], x, y);
  for (const f of EAR_TW) {                            // an ear flicks
    const e = t - f;
    if (e >= 0 && e < 0.5 && frac(e * 6) < 0.5) { ctx.fillStyle = GINGER[1]; ctx.fillRect(x + 16 + turned, y, 3, 2); }
  }
}

/* ================= The neighbours across the street (in the windows under the billboard) ================= */
// [column, row, what goes on, rank]: every one of them is drawn while its rank is under the crowd
const FLATS = [
  { x: 164, y: 156, kind: 'cook', rank: 0.55 }, { x: 181, y: 156, kind: 'tv' }, { x: 198, y: 156, kind: 'read', rank: 0.7 },
  { x: 164, y: 171, kind: 'dark' }, { x: 181, y: 171, kind: 'phone', rank: 0.85 }, { x: 198, y: 171, kind: 'warm' },
];
const FLAT = { w: 13, h: 10 };
function drawFlats(t, crowd) {
  const sil = '#2a1a16', sil2 = '#1c100e';
  for (const f of FLATS) {
    const { x, y } = f, here = f.rank !== undefined && f.rank < crowd;
    if (f.kind === 'dark') { ctx.fillStyle = '#12101f'; ctx.fillRect(x, y, FLAT.w, FLAT.h); ctx.fillStyle = '#1e2a24'; ctx.fillRect(x + 8, y + 5, 3, 5); ctx.fillRect(x + 7, y + 6, 5, 2); continue; }
    if (f.kind === 'tv') {
      ctx.fillStyle = TVC[Math.floor(hash(9, step(t, 480), 2) * TVC.length)]; ctx.globalAlpha = 0.55; ctx.fillRect(x, y, FLAT.w, FLAT.h); ctx.globalAlpha = 1;
      ctx.fillStyle = '#0e0c1c'; ctx.fillRect(x, y, FLAT.w, 2); ctx.fillRect(x + 2, y + 6, 6, 4);   // a pelmet, a sofa's back
    } else {
      ctx.fillStyle = f.kind === 'read' ? '#e8a858' : f.kind === 'phone' ? '#d89a50' : '#f0b060'; ctx.fillRect(x, y, FLAT.w, FLAT.h);
      ctx.fillStyle = '#ffd898'; ctx.fillRect(x, y, FLAT.w, 1);
    }
    if (f.kind === 'warm') { ctx.fillStyle = '#8a4a28'; ctx.fillRect(x, y, 5, FLAT.h); ctx.fillStyle = '#a05a30'; ctx.fillRect(x + 2, y, 1, FLAT.h); }   // a curtain half drawn
    if (f.kind === 'cook') {                           // at the stove: an arm stirs, steam goes up
      ctx.fillStyle = '#5a3a26'; ctx.fillRect(x + 8, y + 6, 5, 4); ctx.fillStyle = '#c8c0b0'; ctx.fillRect(x + 9, y + 5, 3, 1);
      if (here) {
        const stir = frac(t / 2) < 0.5 ? 0 : 1;
        ctx.fillStyle = sil; ctx.fillRect(x + 3, y + 1, 4, 4); ctx.fillRect(x + 2, y + 5, 6, 5);
        ctx.fillStyle = sil2; ctx.fillRect(x + 7, y + 5 + stir, 2, 1);
      }
      ctx.fillStyle = '#fff4e0';
      for (let k = 0; k < 3; k++) { const u = frac(t * 40 / LOOP + k / 3); ctx.globalAlpha = 0.6 * Math.sin(Math.PI * u); ctx.fillRect(x + 10 + Math.round(Math.sin(u * 5 + k)), y + 4 - Math.round(u * 4), 1, 1); }
      ctx.globalAlpha = 1;
    } else if (f.kind === 'read' && here) {            // in an armchair with a book; a page turns
      const turn = frac(t / 30) > 0.97;
      ctx.fillStyle = '#6a3a2a'; ctx.fillRect(x + 1, y + 4, 9, 6);
      ctx.fillStyle = sil; ctx.fillRect(x + 4, y + 1, 4, 4); ctx.fillRect(x + 3, y + 5, 6, 5);
      ctx.fillStyle = '#f4ead0'; ctx.fillRect(x + 8, y + 6 - (turn ? 1 : 0), 3, 2);
    } else if (f.kind === 'phone' && here) {           // pacing the room, a phone to the ear
      const px = x + 1 + Math.round((0.5 + 0.5 * Math.sin(TAU * 8 * t / LOOP)) * 6);
      ctx.fillStyle = sil; ctx.fillRect(px + 1, y + 1, 4, 4); ctx.fillRect(px, y + 5, 6, 5);
      ctx.fillStyle = '#cfe0ff'; ctx.fillRect(px + 5, y + 2, 1, 2);
    }
    ctx.fillStyle = '#1a1530'; ctx.fillRect(x + 6, y, 1, FLAT.h);   // the sash bar
  }
}

/* ================= The glass: mist along the bottom of each pane, beads that cling, drops that run ================= */
function renderMist() {
  const p = pix(W, H);
  for (const q of PANES) for (let x = q.x0; x < q.x1; x++) {
    const yb = glassBot(x), yt = glassTop(x), edge = Math.min(x - q.x0, q.x1 - 1 - x);
    for (let y = yt; y < yb; y++) {
      const f = 0.95 * Math.max(0, 1 - (yb - y) / 15) + 0.6 * Math.max(0, 1 - edge / 6) * Math.max(0, 1 - (yb - y) / 44) + (vnoise(x / 4, y / 3, 131) - 0.5) * 0.3;
      const lv = Math.floor(clamp(f, 0, 1) * 4 + bayer(x, y) - 0.3) / 4;
      if (lv > 0) p.set(x, y, hash(x, y, 133) < 0.05 * lv ? '#e4ecfa' : '#a6aecc', Math.round(Math.min(1, lv) * 0.4 * 255));
    }
  }
  return trim(p);
}
/** snow lying on the sill outside, deepest in the corners of each pane */
function renderSnowSill() {
  const p = pix(W, H);
  for (const q of PANES) for (let x = q.x0; x < q.x1; x++) {
    const edge = Math.min(x - q.x0, q.x1 - 1 - x), d = Math.round(2 + 3 * Math.max(0, 1 - edge / 7) + 1.6 * vnoise(x / 5, 0, 151)), yb = glassBot(x);
    for (let j = 1; j <= d; j++) p.set(x, yb - j, j === d ? '#f4f8ff' : j > d - 2 ? '#d6e0f6' : '#aab8dc');
  }
  return trim(p);
}
const BEADS = [], RUNNERS = [];
{
  const r = rng(501), spanW = PANES.reduce((s, q) => s + q.x1 - q.x0, 0);
  const paneOf = u => { let a = u * spanW; for (const q of PANES) { if (a < q.x1 - q.x0) return q; a -= q.x1 - q.x0; } return PANES[2]; };
  for (let i = 0; i < 230; i++) { const u = r(); BEADS.push({ id: i, n: 3 + Math.floor(r() * 7), ph: r(), s: u < 0.5 ? 1 : u < 0.88 ? 2 : 3, pane: paneOf }); }
  for (let i = 0; i < 34; i++) RUNNERS.push({ id: 1000 + i, n: 4 + Math.floor(r() * 5), ph: r(), pane: paneOf });
}
const DROP_COOL = ['#d4dff6', '#7f8cb4'], DROP_WARM = ['#ffe2ac', '#b08a5c'];
const warmAt = x => x > 286 || x < 100;               // drops by a lamp take its colour
function bead(x, y, s, a) {
  const [hi, mid] = warmAt(x) ? DROP_WARM : DROP_COOL;
  ctx.globalAlpha = a * (s === 1 ? 0.34 : 0.8); ctx.fillStyle = hi; ctx.fillRect(x, y, 1, 1);
  if (s === 1) return;
  ctx.globalAlpha = a * 0.34; ctx.fillStyle = mid; ctx.fillRect(x, y + 1, 1, 1);
  if (s > 2) ctx.fillRect(x + 1, y, 1, 2);
  ctx.globalAlpha = a * 0.4; ctx.fillStyle = '#04060e'; ctx.fillRect(x, y + 2, s > 2 ? 2 : 1, 1);   // the dark rim under it
}
/** `wet` 0..1.4: how much of the rain is on the glass (drizzle 0.4, rain 1, a storm 1.4); `snow`: it lies on the sill;
 *  `board`: the billboard is up, and the drops keep off its face so that its words always read whole */
function drawGlass(t, wet, snow, board) {
  const sign = (x, y) => board && x >= BB.x - 3 && x <= BB.x + BB.w + 1 && y >= BB.y - 4 && y <= BB.y + BB.h + 1;
  ctx.globalAlpha = 0.85 + 0.15 * wave(t, 3, 0.2);
  blit(MIST_C);
  ctx.globalAlpha = 1;
  if (snow) blit(SNOW_C);
  if (wet > 0) {
    for (let i = 0, n = Math.min(BEADS.length, Math.round(160 * wet)); i < n; i++) {
      const b = BEADS[i], { cyc, u } = cycle(t, b.n, b.ph);
      if (u > 0.94) continue;
      const q = b.pane(hash(b.id, cyc, 1)), x = q.x0 + 1 + Math.floor(hash(b.id, cyc, 2) * (q.x1 - q.x0 - 2 - b.s)), yt = glassTop(x) + 1;
      const y = yt + Math.floor(hash(b.id, cyc, 3) * (glassBot(x) - 4 - b.s - yt));
      if (!sign(x, y)) bead(x, y, u < 0.004 ? b.s + 1 : b.s, Math.min(1, (0.94 - u) / 0.05));
    }
    for (let i = 0, n = Math.min(RUNNERS.length, Math.round(24 * wet)); i < n; i++) {
      // a drop that slides in three or four goes, resting between them, and leaves a wet trail that dries behind it
      const g = RUNNERS[i], { cyc, u } = cycle(t, g.n, g.ph), T = LOOP / g.n, tau = u * T, id = g.id;
      const q = g.pane(hash(id, cyc, 1)), x0 = q.x0 + 3 + Math.floor(hash(id, cyc, 2) * (q.x1 - q.x0 - 7));
      const yt = glassTop(x0) + 2, yEnd = glassBot(x0) - 3, y0 = yt + Math.floor(hash(id, cyc, 3) * (yEnd - yt) * 0.45), K = 3 + Math.floor(hash(id, cyc, 4) * 2);
      if (sign(x0, Math.max(y0, BB.y))) continue;
      let tk = T * (0.05 + 0.2 * hash(id, cyc, 5)), y = y0, x = x0, done = tau < tk, gone = 0;
      for (let k = 0; k < K && !done; k++) {
        const dur = 0.4 + 0.8 * hash(id, cyc, 20 + k), rest = 0.8 + 3.2 * hash(id, cyc, 30 + k), dy = (yEnd - y0) * (0.6 + hash(id, cyc, 10 + k)) / (K * 1.1);
        if (hash(id, cyc, 40 + k) < 0.3) x += hash(id, cyc, 50 + k) < 0.5 ? -1 : 1;
        const s = (tau - tk) / dur;
        if (s < 1) { y += dy * s * s * (3 - 2 * s); done = true; }
        else { y += dy; tk += dur + rest; if (tau < tk) done = true; else if (k === K - 1) gone = (tau - tk) / Math.max(0.01, T - tk); }
      }
      if (tau < T * 0.05) continue;
      y = Math.min(y, yEnd);
      const Y = Math.round(y), fade = 1 - gone, [hi] = warmAt(x) ? DROP_WARM : DROP_COOL;
      const from = Math.max(y0, Y - 30);               // the trail dries from its top
      ctx.globalCompositeOperation = 'lighter'; ctx.fillStyle = '#141c38';
      ctx.globalAlpha = 0.5 * fade; ctx.fillRect(x, from, 1, Math.max(0, Y - from));
      ctx.globalAlpha = 0.5 * fade; ctx.fillRect(x, Math.max(from, Y - 12), 1, Math.max(0, Math.min(12, Y - from)));
      ctx.globalCompositeOperation = 'source-over'; ctx.fillStyle = hi;
      for (let yy = from + 2; yy < Y - 2; yy += 5 + (hash(x, yy, 3) * 4 | 0)) { ctx.globalAlpha = 0.3 * fade; ctx.fillRect(x, yy, 1, 1); }   // beads left in the trail
      bead(x, Y, 3, fade);
    }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
  // the room, faintly, in the glass: the floor lamp and the little lamp on the desk
  halo(HL.refl, 326, 126, 0.6); halo(HL.refl, 96, 150, 0.4);
}

/* ================= Per-frame: the room's own life ================= */
function drawCurtain(t) {                              // the right-hand curtain stirs: each strip a little further off than the one above
  const { c, x, y } = CURTAIN, n = Math.ceil(c.height / 6), sw = wave(t, 20, 0.2) * 1.4 + wave(t, 48, 0.6) * 0.6;
  for (let k = 0; k < n; k++) ctx.drawImage(c, 0, k * 6, c.width, 6, x + Math.round(sw * (k / n) ** 2), y + k * 6, c.width, 6);
}
function drawRecord(t) {
  const [tx, ty] = TURNTABLE, a = TAU * 133 * t / LOOP;   // 33 1/3 turns a minute: 133 a loop
  ctx.fillStyle = '#f2cfa8'; ctx.fillRect(Math.round(tx + Math.cos(a) * 3), Math.round(ty - 3 + Math.sin(a) * 0.8), 1, 1);
  ctx.fillStyle = '#5a5a6a';
  for (const [rr, o] of [[9, 1], [12.5, 3.4]]) ctx.fillRect(Math.round(tx + Math.cos(a + o) * rr), Math.round(ty - 3 + Math.sin(a + o) * rr * 0.17), 1, 1);
  ctx.fillStyle = '#c8c0b0'; ctx.fillRect(tx + 9, ty - 6, 1, 3); ctx.fillRect(tx + 6, ty - 4, 4, 1);   // the arm
  ctx.fillStyle = '#ff6a3a'; ctx.fillRect(tx - 17, ty, 1, 1); halo(HL.led, tx - 17, ty, 0.7 + 0.3 * wave(t, 60));
}
const MOTES = [];
{ const r = rng(701); for (let i = 0; i < 26; i++) MOTES.push({ id: i, n: 2 + Math.floor(r() * 3), ph: r(), k1: 5 + Math.floor(r() * 9), k2: 6 + Math.floor(r() * 11), p1: r(), p2: r() }); }
function drawDust(t) {                                 // dust in the floor lamp's light
  ctx.fillStyle = '#ffdca0';
  for (const m of MOTES) {
    const { cyc, u } = cycle(t, m.n, m.ph), bx = FLAMP_AT[0] - 62 + hash(m.id, cyc, 1) * 120, by = FLAMP_AT[1] - 46 + hash(m.id, cyc, 2) * 110;
    const x = Math.round(bx + (u - 0.5) * 22 + wave(t, m.k1, m.p1) * 3), y = Math.round(by + (u - 0.5) * (hash(m.id, cyc, 3) - 0.6) * 26 + wave(t, m.k2, m.p2) * 2.5);
    const d = Math.hypot(x - FLAMP_AT[0], y - FLAMP_AT[1]);
    ctx.globalAlpha = Math.min(1, u / 0.1, (1 - u) / 0.1) * clamp(1.1 - d / 75, 0, 0.8);
    ctx.fillRect(x, y, 1, 1);
  }
  ctx.globalAlpha = 1;
}
/** the lamps' light as a layer added over the room: the desk lamp's cone and its pool, the little lamp's glow */
function renderGlow() {
  const p = pix(W, H), [hx, hy] = R(P(...DHEAD)), [px, py] = R(P(DHEAD[0] - 0.42, DESK.top, 0.36)), c = C('#ffb458');
  fillPoly([[hx - 4, hy + 3], [hx + 3, hy + 3], [px + 34, py + 3], [px - 30, py + 3]], (x, y) => {
    const q = Math.floor(Math.max(0, 1 - Math.hypot(x - hx, y - hy) / 70) * 4 + bayer(x, y)) / 4;
    if (q > 0) p.set(x, y, c, Math.round(Math.min(1, q) * (inGlass(x, y) ? 0.05 : 0.13) * 255));
  });
  return p.done();
}
/** a passing train's light on the ceiling over the window: strongest by the wall, gone a quarter of a metre in */
function renderCeilGlow() {
  const p = pix(W, H), c = C('#ffe2a8'), x0 = UPRIGHTS[0][0] - 6, x1 = CORNER_X - 8;
  for (let x = x0; x < x1; x++) {
    const yc = Math.floor(yAt(x + 0.5, CEIL)), ends = Math.min(1, (x - x0) / 14, (x1 - x) / 14);
    for (let y = Math.max(0, yc - 26); y < yc; y++) {
      const q = Math.floor(Math.pow(1 - (yc - y) / 27, 1.3) * ends * 4 + bayer(x, y) - 0.2) / 4;
      if (q > 0) p.set(x, y, c, Math.round(Math.min(1, q) * 0.24 * 255));
    }
  }
  return trim(p);
}
function drawTrainLight(t) {                          // a soft wash that slides along the ceiling, the other way from the train
  for (let k = 0; k < 2; k++) {
    const tau = t - TRAIN.t[k];
    if (tau < 0 || tau >= TRAIN.dur) continue;
    const I = env(tau, 0, TRAIN.dur, 3), mid = k ? lerp(70, 350, tau / TRAIN.dur) : lerp(350, 70, tau / TRAIN.dur), { c, x, y } = CEIL_C;
    ctx.globalCompositeOperation = 'lighter'; ctx.globalAlpha = I / 6;
    for (const half of [80, 71, 62, 53, 44, 35]) {     // brightest in its middle, its ends in soft steps
      const xa = Math.max(x, Math.round(mid - half)), xb = Math.min(x + c.width, Math.round(mid + half));
      if (xb > xa) ctx.drawImage(c, xa - x, 0, xb - xa, c.height, xa, y, xb - xa, c.height);
    }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }
}
function renderVignette() {
  const p = pix(W, H), c = [8, 4, 6];
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const q = Math.floor(smooth(0.66, 1.42, Math.hypot((i - W * 0.56) / (W / 2), (y - H * 0.47) / (H / 2) * 0.95)) * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, c, Math.round(q * 0.5 * 255));
  }
  return p.done();
}

/* ================= The picked weather (site/js/engine/weather.js) ================= */
// Tokyo's own night is rain, here as there. Every pick falls outside only: before the glass and the room are drawn,
// so the near depth's tint stays on the view. Rain of any kind also beads and runs on the panes, snow lies on the sill,
// leaves cross on the wind (no tree reaches a ninth floor), and they all catch the tower, the expressway's lamps and
// the billboard's floodlight.
const WEATHER = weatherPlans(['rain']);
const WX_LAND = [[150, 160], [166, 178], [H + 4, H + 4]];
const WX_LIGHTS = [{ x: TWR.cx, y: 128, rx: 13, ry: 30, col: '#d89a78' }, { x: 104, y: 174, rx: 50, ry: 12, col: '#ffc080' }];
const WX_BASE = { land: WX_LAND, density: 1.5, leaves: { wind: [UPRIGHTS[0][1], 62, UPRIGHTS[3][0], 150] } };
const WX = { ...WX_BASE, lights: WX_LIGHTS };
// nothing of the nearest depth crosses the sign's face (its edges are the frame's), so its words always read whole
const WX_BB = { ...WX_BASE, lights: [...WX_LIGHTS, { x: BB.x + BB.w / 2, y: BB.y - 6, rx: 26, ry: 8, col: '#ffe6b8' }],
  shelter: [[], [], [[BB.x - 1, BB.y - 1, BB.x + BB.w, BB.y + BB.h]]] };
const WETNESS = { rain: 1, thunderstorm: 1.4, drizzle: 0.4 };

function prerender() {
  HL = {
    tower: makeHalo(56, '#ff6a2a', 0.15, 1.6), towerMid: makeHalo(30, '#ffa060', 0.15, 1.6), deck: makeHalo(13, '#ffd8a0', 0.28), topdeck: makeHalo(8, '#e0f0ff', 0.3),
    lift: makeHalo(4, '#fff0c8', 0.5, 1), bridge: makeHalo(9, '#d8d4ff', 0.22), red: makeHalo(4, '#ff3030', 0.6, 1),
    strobe: makeHalo(3, '#ffffff', 0.7, 1), sodium: makeHalo(6, '#ffa040', 0.34), trainWin: makeHalo(18, '#ffeec0', 0.12),
    bbGlow: makeHalo(30, '#ffd8c0', 0.1), bbLamp: makeHalo(5, '#ffe8c8', 0.5), led: makeHalo(3, '#ff5a30', 0.5, 1),
    flamp: makeHalo(92, '#ffa850', 0.2, 1.7), flampCore: makeHalo(24, '#fff0c8', 0.34, 1.4), dlamp: makeHalo(30, '#ffb060', 0.2, 1.5), dlampCore: makeHalo(8, '#fff4d8', 0.5, 1.3),
    mush: makeHalo(26, '#ffb060', 0.22, 1.5), refl: makeHalo(22, '#ffb070', 0.1, 1.4),
  };
  SKY_C = renderSky();
  CLOUDS = [
    { c: makeClouds(17, 46, { body: '#2b2466', core: '#262060', bot: '#3a2c74' }, 3), y: 42, k: 1, a: 1 },
    { c: makeClouds(29, 52, { body: '#4d2f7c', core: '#452974', bot: '#9a4a8a' }, 4), y: 84, k: 2, a: 1 },
  ];
  FAR = renderFar(); HR = renderHighrises(); TOWER = renderTower(); MID = renderMid(); VIA_C = renderViaduct();
  TRAIN_C = [renderTrain(false), renderTrain(true)]; NEAR = renderNear(); TANK_C = renderTank();
  BB_FRAME = renderBBFrame(); BB_FACE = [renderBBFace(-1), renderBBFace(2)]; BB_PITCH = makePitch(BB.w, BB.h, { accent: '#ff5fa2' });
  const room = pix(W, H);
  paintShell(room); paintWindow(room); paintRail(room);
  paintCurtain(-4.4, -3.88, 0.3, 1.3, (x, y, col) => room.set(x, y, col), 0.06);
  paintSideWall(room); paintFloorLamp(room); paintDesk(room); paintDeskThings(room); paintBed(room);
  ROOM = room.done();
  const cur = pix(W, H);
  paintCurtain(-0.37, -0.05, 0.28, 4.1, (x, y, col) => cur.set(x, y, col));
  CURTAIN = trim(cur);
  MIST_C = renderMist(); SNOW_C = renderSnowSill(); GLOW_C = renderGlow(); CEIL_C = renderCeilGlow(); VIG_C = renderVignette();
  CHAIR_C = renderChair(); BODY_C = renderBody(); HEAD_C = renderHead(); MUG_C = sprite(MUG_ROWS, MUG_KEY);
  HAND_C = { fist: sprite(FIST_ROWS, SKIN_KEY), open: sprite(OPEN_ROWS, SKIN_KEY), grip: sprite(GRIP_ROWS, SKIN_KEY) };
  CAT_C = [renderCat(0), renderCat(1)];
  PUFF = [null, makePuff(1, '#f4ead8', 1), makePuff(2, '#f4ead8', 1)];
}

/* ================= Frame compositor (back to front) ================= */
/** render(t) paints the city's own billboard ad; { pitch } paints a face of the site's over it; { billboard: false }
 *  leaves the sign out (the loop videos); { weather } draws a WEATHERS pick, the city's own when left out; { crowd }
 *  thins the people (the neighbours first, then whoever is at the desk); { clock } sets the wall clock */
function renderFrame(t, { pitch = false, billboard = true, weather, crowd = CROWD, reducedMotion = false, clock } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX, kind = wx.rain ? 'rain' : wx.shared, wet = WETNESS[kind] || 0;
  drawSky(t);
  drawFar(t);
  drawWeather(ctx, kind, 0, t, wxc, reducedMotion);
  drawHighrises(t);
  drawTower(t, crowd);
  drawMid(t);
  drawWeather(ctx, kind, 1, t, wxc, reducedMotion);
  blit(VIA_C);
  drawTrain(t);
  drawNear(billboard);
  drawFlats(t, crowd);
  if (billboard) drawBillboard(t, pitch, wet > 0);
  drawBlinkers(t, billboard);
  drawWeather(ctx, kind, 2, t, wxc, reducedMotion);
  drawGlass(t, wet, kind === 'snow', billboard);
  ctx.drawImage(ROOM, 0, 0);
  drawTrainLight(t);
  drawCurtain(t);
  hands(ctx, CLOCK[0], CLOCK[1], clock, 610, 3, 5, '#2a1c14');
  drawRecord(t);
  drawCat(t);
  drawSitter(t, crowd);
  ctx.globalCompositeOperation = 'lighter'; ctx.drawImage(GLOW_C, 0, 0); ctx.globalCompositeOperation = 'source-over';
  const breath = 0.96 + 0.04 * wave(t, 16, 0.3), [hx, hy] = R(P(...DHEAD)), [mx, my] = R(P(MUSH.du, DESK.top + 0.2, MUSH.w));
  halo(HL.flamp, FLAMP_AT[0], FLAMP_AT[1], breath); halo(HL.flampCore, FLAMP_AT[0], FLAMP_AT[1], 0.9);
  halo(HL.dlamp, hx - 1, hy + 4, 0.95); halo(HL.dlampCore, hx - 1, hy + 3, 0.9);
  halo(HL.mush, mx, my, 0.9 * breath);
  drawDust(t);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'tokyo-room',
  name: 'Tokyo',
  tagline: 'Warm room, rain on the glass',
  timeZone: 'Asia/Tokyo',
  accent: '#ff5fa2',
  focusX: TWR.cx,
  landmark: [TWR.cx - 14, TWR.cx + 15],               // Tokyo Tower's feet
  landmarkTop: TWR.tip,                                // the row of the antenna's tip, which landscape crops keep
  billboard: BB,
  crowd: CROWD,
  indoor: true,                                        // heard from inside: the city's sounds come through the wall and the glass (site/js/audio/ambience.js)
  spots: [                                             // what answers a tap (site/js/ui/spots.js)
    { kind: 'landmark', x: TWR.cx - 9, y: TWR.tip, w: 19, h: TWR.base - TWR.tip },      // Tokyo Tower, through the glass
    { kind: 'window', x: FLATS[2].x, y: FLATS[2].y, w: FLAT.w, h: FLAT.h },              // the reader's window across the street
    { kind: 'cat', x: CATX - 11, y: CATY - 30, w: 23, h: 30 },                           // on the sill
    { kind: 'window', x: FLAMP_AT[0] - 14, y: SHADE[0], w: 29, h: SHADE[1] - SHADE[0] + 1 },   // the floor lamp's shade
  ],
  ambience: { rain: 0.5, traffic: 0.25, wind: 0.1 },
  events: TRAIN.t.map(t => ({ t, type: 'train' })),
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};

